import React, { useState, useRef, useMemo, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box, Typography, Paper, Button, ButtonGroup, Alert, Chip, Stack, Divider,
  ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, CircularProgress, LinearProgress, IconButton, Tooltip, useTheme,
} from '@mui/material';
import { InsertDriveFile, Refresh, CheckCircle, RadioButtonUnchecked, Download, ArrowUpward, ArrowDownward, Delete as DeleteIcon, FolderOpen, QuestionAnswer } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { ComposedChart, Bar, Line, LineChart, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import MassBalanceMapping from './MassBalanceMapping';
import {
  persistUploads, listPeriods, listUploads, deleteUpload, fetchStoredFile, StoredFile, MBAuthError, MBTabHandle,
  fetchTrendSeries, fetchPeriodSnapshot,
  fetchLossSeries, LossSeries, fetchMbSettings, MbSettings, DEFAULT_MB_MARGIN_PCT,
} from '../utils/massBalanceStore';
import { getUnitSystem } from '../utils/units';
import {
  ProductMapping, defaultMapping, loadMapping, saveMapping, heuristicSubCat, CATEGORIES, SUBCATS, catLabel,
} from '../utils/massBalanceMapping';
import { classify as classifyOas, YieldData, InvRow } from '../utils/massBalanceOas';
import { parseFuelLoss, FuelLossReport } from '../utils/massBalanceFuelLoss';
import FuelLossPanel from './FuelLossPanel';
import { Stream, BalanceRow, computeBalance, yieldDataToStreams, groupSubCat, computeYield as computeYieldPure, buildOasBalances, pickYieldsByBasis } from '../utils/massBalanceCompute';
import { useCalChat } from '../contexts/CalChatContext';
import MonthEndChecklist from './MonthEndChecklist';
import MassGainLossOverview from './MassGainLossOverview';

// ---------- helpers ----------
const MONTHS = ['january','february','march','april','may','june','july','august','september','october','november','december'];
const norm = (s: any) => String(s ?? '').trim().toLowerCase();
const numAt = (row: any[], i: number): number => {
  const v = row?.[i];
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};
const fmt = (n: number) => (n === 0 ? '0' : n.toLocaleString(undefined, { maximumFractionDigits: 0 }));
const CAT_COLOR: Record<string, string> = { feedstock: '#a0672c', intermediates: '#8894a6', products: '#4a7fbf', byproducts: '#c0392b' };

// ---------- production report parsing (the AORA report we replicate) ----------
// Volume tab (bbls) / Mass tab (lbs). Columns (0-indexed):
//   0 product | 5 inv monthly initial (opening) | 6 inv monthly final (closing)
//   10 movements monthly shipments | 11 monthly receipts | 12 monthly reclass | 15 monthly production
function parseProductionTab(ws: XLSX.WorkSheet): Stream[] {
  const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, blankrows: false });
  const out: Stream[] = [];
  let started = false;
  for (const row of rows) {
    const name = row?.[0];
    if (!started) { if (norm(name) === 'products') started = true; continue; }
    if (!name || typeof name !== 'string') continue;
    // a data row has at least one numeric inventory/production cell
    const hasNum = [5, 6, 10, 11, 12, 15].some((i) => typeof row[i] === 'number');
    if (!hasNum) continue;
    out.push({
      product: String(name).trim(),
      opening: numAt(row, 5), closing: numAt(row, 6),
      ship: numAt(row, 10), rec: numAt(row, 11), recl: numAt(row, 12), prod: numAt(row, 15),
    });
  }
  return out;
}

// ---------- movement report parsing (independent Receipts/Sales/Consumption) ----------
interface MovAgg { code: string; desc: string; receipts: number; sales: number; consumption: number; }
function parseMovements(wb: XLSX.WorkBook, year: number, month: number, wholeFile = false): { byCode: Record<string, MovAgg>; totals: any; months: Set<string> } {
  const sheetName = ['Formatted Report', 'Formatted', 'Manipulated', 'Consolidated'].find((n) => wb.SheetNames.includes(n)) || wb.SheetNames[0];
  const rows = XLSX.utils.sheet_to_json<any[]>(wb.Sheets[sheetName], { header: 1, blankrows: false });
  // find header row (contains 'Category')
  let hdr = -1;
  for (let i = 0; i < rows.length; i++) { if (rows[i].map(norm).includes('category')) { hdr = i; break; } }
  const byCode: Record<string, MovAgg> = {};
  const totals = { Receipts: 0, Sales: 0, Consumptions: 0 };
  const months = new Set<string>();
  for (let i = hdr + 1; i < rows.length; i++) {
    const row = rows[i];
    const cat = String(row?.[2] ?? '').trim();
    if (!['Receipts', 'Sales', 'Consumptions'].includes(cat)) continue;
    // date -> year/month
    const d = row[3];
    let y: number | null = null, m: number | null = null;
    if (d instanceof Date) { y = d.getFullYear(); m = d.getMonth() + 1; }
    else if (typeof d === 'string' && d.length >= 7) { y = parseInt(d.slice(0, 4)); m = parseInt(d.slice(5, 7)); }
    if (y && m) months.add(`${y}-${String(m).padStart(2, '0')}`);
    // wholeFile: the file IS a single month (matched by filename) — aggregate every row
    // even if the internal date column is unparseable/inconsistent.
    if (!wholeFile && (y !== year || m !== month)) continue;
    const code = String(row[0] ?? '').trim();
    const desc = String(row[1] ?? '').trim();
    const vol = numAt(row, 7) || numAt(row, 10);
    if (!byCode[code]) byCode[code] = { code, desc, receipts: 0, sales: 0, consumption: 0 };
    if (cat === 'Receipts') { byCode[code].receipts += vol; totals.Receipts += vol; }
    else if (cat === 'Sales') { byCode[code].sales += vol; totals.Sales += vol; }
    else { byCode[code].consumption += vol; totals.Consumptions += vol; }
  }
  return { byCode, totals, months };
}

// per-transaction dated movement rows (for Day/Week views). basis picks volume vs mass column.
interface MovRow { dateKey: string; code: string; desc: string; category: 'Receipts' | 'Sales' | 'Consumptions'; vol: number; }
function parseMovementRowsDated(wb: XLSX.WorkBook, basis: 'volume' | 'mass'): MovRow[] {
  const sheetName = ['Formatted Report', 'Formatted', 'Manipulated', 'Consolidated'].find((n) => wb.SheetNames.includes(n)) || wb.SheetNames[0];
  const rows = XLSX.utils.sheet_to_json<any[]>(wb.Sheets[sheetName], { header: 1, blankrows: false });
  let hdr = -1;
  for (let i = 0; i < rows.length; i++) { if (rows[i].map(norm).includes('category')) { hdr = i; break; } }
  const out: MovRow[] = [];
  for (let i = hdr + 1; i < rows.length; i++) {
    const row = rows[i];
    const cat = String(row?.[2] ?? '').trim();
    if (!['Receipts', 'Sales', 'Consumptions'].includes(cat)) continue;
    const d = row[3];
    let dateKey = '';
    if (d instanceof Date) dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    else if (typeof d === 'string' && d.length >= 10) dateKey = d.slice(0, 10);
    if (!dateKey) continue;
    const vol = basis === 'mass' ? numAt(row, 12) : (numAt(row, 7) || numAt(row, 10));
    out.push({ dateKey, code: String(row[0] ?? '').trim(), desc: String(row[1] ?? '').trim(), category: cat as any, vol });
  }
  return out;
}


// ---------- period matching across many files ----------
// key = "YYYY-MM"
const detectPeriodKey = (name: string): string | null => {
  const m = name.match(/([A-Za-z]+)\s+(\d{4})/);            // "April 2025"
  if (m) { const mi = MONTHS.indexOf(m[1].toLowerCase()); if (mi >= 0) return `${m[2]}-${String(mi + 1).padStart(2, '0')}`; }
  const m2 = name.match(/(20\d{2})[-_ ]?(0[1-9]|1[0-2])\b/); // "2025-04" / "2025 04"
  if (m2) return `${m2[1]}-${m2[2]}`;
  const m3 = name.match(/\b(2\d)[ _-](0[1-9]|1[0-2])\b/);    // "25 04" (YY MM)
  if (m3) return `20${m3[1]}-${m3[2]}`;
  return null;
};
// "2026-08" -> "August 2026". Anything that isn't a YYYY-MM key (a range label, a legacy 'unknown' period,
// null) is passed through untouched — this used to throw and take the whole tab down with it.
const keyLabel = (key: string | null | undefined) => {
  const m = /^(\d{4})-(\d{2})$/.exec(key || '');
  const n = m ? MONTHS[+m[2] - 1] : undefined;
  return n ? `${n[0].toUpperCase()}${n.slice(1)} ${m![1]}` : (key || '—');
};
// months present in a movement workbook (parseMovements collects them regardless of filter)
const movementMonths = (wb: XLSX.WorkBook): Set<string> => parseMovements(wb, -1, -1).months;
const prevMonthKey = (key: string) => { let [y, m] = key.split('-').map(Number); m -= 1; if (m < 1) { m = 12; y -= 1; } return `${y}-${String(m).padStart(2, '0')}`; };

// ---------- inventory (tank-gauging) snapshot parsing ----------
// Sheet dtkivv.rpt: product-group header → tank rows → "SubTotals:" row (Net bbls in col 1).
// A snapshot is a single as-of date ("For: <weekday>, Month D, YYYY"); Net Bbls only.
interface InvSnap { dateKey: string; monthKey: string; eom: boolean; byProduct: Record<string, number>; total: number; }
function parseInventory(wb: XLSX.WorkBook): InvSnap | null {
  const rows = XLSX.utils.sheet_to_json<any[]>(wb.Sheets[wb.SheetNames[0]], { header: 1, blankrows: false });
  let dateKey: string | null = null;
  for (let i = 0; i < Math.min(rows.length, 12) && !dateKey; i++) {
    for (const c of rows[i]) {
      if (typeof c === 'string' && /for:/i.test(c)) {
        const m = c.match(/([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})/);
        if (m) { const mi = MONTHS.indexOf(m[1].toLowerCase()); if (mi >= 0) { dateKey = `${m[3]}-${String(mi + 1).padStart(2, '0')}-${String(+m[2]).padStart(2, '0')}`; break; } }
      }
    }
  }
  const skip = new Set(['products', 'tanks', 'subtotals:', 'volume inventory', '']);
  let cur: string | null = null; const byProduct: Record<string, number> = {}; let total = 0; let started = false;
  for (const row of rows) {
    const c0 = String(row?.[0] ?? '').trim(); const lc = c0.toLowerCase();
    if (lc === 'tanks') { started = true; continue; }
    if (!started) continue;
    if (lc === 'subtotals:') { if (cur) { const net = numAt(row, 1); byProduct[cur] = (byProduct[cur] || 0) + net; total += net; } continue; }
    if (typeof row[1] === 'number') continue;   // tank detail row
    if (skip.has(lc)) continue;
    cur = c0;                                    // product-group header
  }
  if (!dateKey && Object.keys(byProduct).length === 0) return null;
  const day = dateKey ? +dateKey.slice(8, 10) : 0;
  return { dateKey: dateKey || '', monthKey: dateKey ? dateKey.slice(0, 7) : '', eom: day >= 26, byProduct, total };
}

interface ProdEntry { file: File; wb: XLSX.WorkBook; key: string; }
interface MovEntry { file: File; wb: XLSX.WorkBook; months: Set<string>; fileMonth: string | null; internalMonths: Set<string>; }
interface InvEntry { file: File; wb: XLSX.WorkBook; snap: InvSnap; }
interface PeriodMatch {
  key: string; label: string; prod: ProdEntry; mov: MovEntry | null; ready: boolean;
  openInv: InvEntry | null; closeInv: InvEntry | null;
}

// ---------- trend aggregation (Month / Quarter / Annual / 12-mo rolling) ----------
// 'day' and 'week' are no longer OFFERED: both fall back to dated MOVEMENT data and can produce
// neither production nor closure from monthly source files, so they rendered an empty chart. The
// toggles are gone (see the Granularity group below). The values stay in the union so the movement
// code paths still compile -- they are simply unreachable now, and removing them would mean
// unpicking ~15 isDaily call sites for no user-visible gain.
type Gran = 'day' | 'week' | 'month' | 'quarter' | 'annual' | 'rolling12';
const withinTrailing12 = (key: string, endKey: string) => {
  const [ey, em] = endKey.split('-').map(Number); const [ky, km] = key.split('-').map(Number);
  const endIdx = ey * 12 + em, kIdx = ky * 12 + km;
  return kIdx <= endIdx && kIdx > endIdx - 12;
};
// A feedstock's "production" is negative by nature -- crude is CHARGED and consumed, not made. Summing
// every grade therefore gave a negative headline (EET: -361,407 m3, which is -13.8M feedstock almost
// cancelling +13.5M of product). The throughput line below already guarded against this with
// Math.max(0, ...); the headline did not.
const isFeedstock = (category: any) => String(category || '').startsWith('feedstock');

function bucketFromMonths(arr: any[]) {
  const t = { opening: 0, receipts: 0, production: 0, netProduction: 0, shipments: 0, reclass: 0, gainLoss: 0,
              realLoss: 0, crudeProc: 0, realLossMonths: 0 };
  const byProduct: Record<string, any> = {};
  for (const s of arr) {
    t.opening += s.opening; t.receipts += s.receipts; t.production += s.production;
    t.shipments += s.shipments; t.reclass += s.reclass; t.gainLoss += s.gainLoss;
    for (const k in s.byProduct) {
      if (!isFeedstock(s.byProduct[k].category)) t.netProduction += (s.byProduct[k].production || 0);
    }
    // Physical loss from the site's own fuel & loss accounting (see fetchLossSeries). Summed as an
    // AMOUNT over crude processed, so a quarter or year is a weighted figure rather than an average
    // of percentages.
    if (typeof s.realLoss === 'number' && typeof s.crudeProc === 'number' && s.crudeProc) {
      t.realLoss += s.realLoss; t.crudeProc += s.crudeProc; t.realLossMonths += 1;
    }
    for (const k in s.byProduct) {
      const pv = s.byProduct[k];
      const c = byProduct[k] || { production: 0, gainLoss: 0, receipts: 0, shipments: 0, category: pv.category };
      c.production += pv.production; c.gainLoss += pv.gainLoss; c.receipts += pv.receipts; c.shipments += pv.shipments;
      byProduct[k] = c;
    }
  }
  const throughput = t.receipts + Math.max(0, t.production);   // flow-based, aggregates cleanly
  return {
    ...t, throughput, byProduct, months: arr.length,
    // The stock-ledger residual as a % -- nets to ~0 BY CONSTRUCTION, so it is a data-integrity
    // check, never the physical loss. Kept and labelled as such.
    ledgerPct: throughput ? (t.gainLoss / throughput) * 100 : 0,
    // What the user actually means by closure: physical loss over crude processed. null when the
    // period has no fuel & loss source, so the UI can fall back and SAY it is falling back.
    closurePct: t.crudeProc ? (t.realLoss / t.crudeProc) * 100
                            : (throughput ? (t.gainLoss / throughput) * 100 : 0),
    hasRealLoss: t.crudeProc > 0,
  };
}
function aggregateSeries(series: any[] | null, gran: Gran) {
  if (!series || !series.length) return [] as any[];
  if (gran === 'month') return series.map((s) => ({ label: keyLabel(s.key), key: s.key, ...bucketFromMonths([s]) }));
  if (gran === 'rolling12') return series.map((s) => ({ label: keyLabel(s.key), key: s.key, ...bucketFromMonths(series.filter((x) => withinTrailing12(x.key, s.key))) }));
  const groups = new Map<string, any[]>();
  for (const s of series) {
    const [y, m] = s.key.split('-').map(Number);
    const gk = gran === 'annual' ? `${y}` : `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
    if (!groups.has(gk)) groups.set(gk, []);
    groups.get(gk)!.push(s);
  }
  return Array.from(groups.keys()).sort().map((gk) => ({ label: gk, key: gk, ...bucketFromMonths(groups.get(gk)!) }));
}

// Module-level cache so uploaded workbooks survive this component unmounting (navigating away
// from the Mass Balance section and back). Seeded into state on mount, mirrored on change; Reset
// clears it. Session-only — nothing persisted server-side.
const mbuCache: { prod: ProdEntry[]; mov: MovEntry[]; inv: InvEntry[] } = { prod: [], mov: [], inv: [] };

// The raw workbooks ARE persisted server-side (persistUploads('refinery', dataset, ...), keyed by period).
// RLAST_PERIOD_KEY remembers the period last loaded from the manager (cosmetic).
const RLAST_PERIOD_KEY = 'refinery.lastPeriod';

const MassBalanceUtility = forwardRef<MBTabHandle, { dataset: string; reloadKey?: number; onUploaded?: () => void; isActive?: boolean }>(({ dataset, reloadKey, onUploaded, isActive = true }, ref) => {
  const [prodEntries, setProdEntries] = useState<ProdEntry[]>(mbuCache.prod);
  const [movEntries, setMovEntries] = useState<MovEntry[]>(mbuCache.mov);
  const [invEntries, setInvEntries] = useState<InvEntry[]>(mbuCache.inv);
  useEffect(() => { mbuCache.prod = prodEntries; }, [prodEntries]);
  useEffect(() => { mbuCache.mov = movEntries; }, [movEntries]);
  useEffect(() => { mbuCache.inv = invEntries; }, [invEntries]);
  const [basis, setBasis] = useState<'volume' | 'mass'>('volume');
  const { setViewContext: calSetViewContext } = useCalChat();
  // The month-end control opens as its own panel (checklist + a Cal chat underneath) rather than
  // firing one summary question into the floating chat.
  const [checklistOpen, setChecklistOpen] = useState(false);

  // Display-only unit labels from the user's global unit-system preference
  // (imperial = Barrels/Pounds, metric = m³/MT). No value conversion -- the uploaded
  // workbook's Volume/Mass sheets already hold whatever unit the site reports in.
  const isMetric = getUnitSystem() === 'metric';
  const [hideInactive, setHideInactive] = useState(true);
  const [momCompare, setMomCompare] = useState(false);
  const [slateMode, setSlateMode] = useState<'share' | 'cumulative'>('share'); // Product-slate bars: per-row share vs cumulative stack
  const [viewMode, setViewMode] = useState<'period' | 'range' | 'trends'>('period');
  const [trends, setTrends] = useState<any[] | null>(null);
  // Physical loss per period, one backend call from the stored analytics snapshots. Trends builds
  // its own series by re-parsing workbooks, which never carry fuel & loss, so without this the
  // closure cards fell back to the ledger residual and read -0.000% every period.
  const [lossSeries, setLossSeries] = useState<LossSeries | null>(null);
  // Per-dataset Mass Balance settings (the loss margin). Decoration only: a missing or failed fetch
  // leaves the chart on its +/-2 sigma fallback rather than blocking anything.
  const [mbSettings, setMbSettings] = useState<MbSettings | null>(null);
  const [gran, setGran] = useState<Gran>('month');
  const [drill, setDrill] = useState<string | null>(null);
  const [chartMode, setChartMode] = useState<'total' | 'stackGroup' | 'single'>('total');
  const [chartSel, setChartSel] = useState<string>('');       // selected group (sub-category id)
  const [chartProd, setChartProd] = useState<string>('ALL');  // 'ALL' or a specific product in the group
  const [critPct, setCritPct] = useState<number>(() => Number(localStorage.getItem('mb-crit-pct')) || 2);
  const [fromDate, setFromDate] = useState<string>('');   // '' = full uploaded coverage
  const [toDate, setToDate] = useState<string>('');
  const [result, setResult] = useState<any>(null);

  // Tell Cal what is on screen, so a question asked while viewing August is answered about August
  // rather than whichever period the model picks. Re-published whenever the dataset or month
  // changes, and cleared on unmount so a stale period never leaks into an unrelated conversation.
  // Only the VISIBLE tab publishes. The hub keeps both tabs mounted (display:none) so uploaded
  // data survives switching, which meant both were publishing and the last effect to run won --
  // a question about the Refinery view could arrive carrying the Diagnostic tab's period.
  // React runs all cleanups before all effects within a commit, so on a tab switch the outgoing
  // tab clears and the incoming one publishes, in that order.
  useEffect(() => {
    if (!isActive) return;
    calSetViewContext({ massBalance: { dataset: dataset || undefined, period: result?.key || undefined,
                                       view: 'Refinery Mass Balance' } });
    return () => calSetViewContext({});
  }, [isActive, dataset, result?.key, calSetViewContext]);

  const [error, setError] = useState<string | null>(null);
  // Unit labels. OAS-sourced data is always metric (m³ / tonnes), regardless of the global
  // imperial/metric preference — label it correctly so tonnes aren't shown as "lbs".
  const oasAssignments = result?.oasAssignments as Record<string, string> | undefined; // OAS group→subcat (categorize by the Daily-Yields Group)
  const useMetric = isMetric || !!result?.oas;
  const volUnit = useMetric ? 'm³' : 'bbls';
  const massUnit = useMetric ? 'MT' : 'lbs';
  const volToggle = useMetric ? 'm³' : 'Barrels';
  const massToggle = useMetric ? 'MT' : 'Pounds';
  const [loading, setLoading] = useState(false);
  // Long operations (trends / range / month load) report what they are doing so the UI never looks frozen.
  const [progress, setProgress] = useState<{ label: string; done: number; total: number } | null>(null);
  const [mapping, setMapping] = useState<ProductMapping | null>(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappingSaved, setMappingSaved] = useState(false);   // true once loaded-from/written-to backend
  const prodInput = useRef<HTMLInputElement>(null);
  const movInput = useRef<HTMLInputElement>(null);
  const invInput = useRef<HTMLInputElement>(null);
  const { getIdTokenClaims } = useAuth0();

  const idToken = async () => (await getIdTokenClaims())?.__raw;

  // Load this dataset's product mapping (reloads when the active dataset changes).
  useEffect(() => {
    if (!dataset) return;
    (async () => { const m = await loadMapping(dataset, await idToken()); if (m) { setMapping(m); setMappingSaved(true); } else { setMapping(defaultMapping()); setMappingSaved(false); } })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset, reloadKey]);

  const readWb = async (file: File) => XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });

  const onProd = async (files: File[], persist = true) => {
    const added: ProdEntry[] = []; const failed: string[] = [];
    for (const file of files) {
      try {
        const wb = await readWb(file);
        if (!wb.SheetNames.includes('Volume') || !wb.SheetNames.includes('Mass')) { failed.push(`${file.name} — no Volume/Mass tabs`); continue; }
        const key = detectPeriodKey(file.name);
        if (!key) { failed.push(`${file.name} — couldn't read a month from the filename`); continue; }
        added.push({ file, wb, key });
      } catch { failed.push(`${file.name} — unreadable (re-save as .xlsx)`); }
    }
    setProdEntries((prev) => { const map = new Map(prev.map((e) => [e.key, e])); added.forEach((e) => map.set(e.key, e)); return Array.from(map.values()); });
    setError(failed.length ? `Skipped ${failed.length} production file(s): ${failed.join('; ')}` : null);
    if (persist) await persistRefinery(added.map((e) => ({ file: e.file, kind: 'production', period: e.key })));
  };
  const onMov = async (files: File[], persist = true) => {
    const added: MovEntry[] = []; const failed: string[] = [];
    for (const file of files) {
      try {
        const wb = await readWb(file);
        const internalMonths = movementMonths(wb);      // months actually present in the data
        const months = new Set(internalMonths);
        const fileMonth = detectPeriodKey(file.name);   // filename period is a reliable coverage signal
        if (fileMonth) months.add(fileMonth);
        added.push({ file, wb, months, fileMonth, internalMonths });
      } catch { failed.push(`${file.name} — unreadable (re-save as .xlsx)`); }
    }
    setMovEntries((prev) => { const map = new Map(prev.map((e) => [e.file.name, e])); added.forEach((e) => map.set(e.file.name, e)); return Array.from(map.values()); });
    if (failed.length) setError((p) => [p, `Skipped ${failed.length} movement file(s): ${failed.join('; ')}`].filter(Boolean).join(' | '));
    if (persist) await persistRefinery(added.map((e) => ({ file: e.file, kind: 'movement', period: e.fileMonth || Array.from(e.internalMonths)[0] || 'unknown' })));
  };

  const onInv = async (files: File[], persist = true) => {
    const added: InvEntry[] = []; const failed: string[] = [];
    for (const file of files) {
      try {
        const wb = await readWb(file);
        let snap = parseInventory(wb);
        if (!snap || !snap.monthKey) {
          const k = detectPeriodKey(file.name);
          if (!k) { failed.push(`${file.name} — couldn't read a snapshot date`); continue; }
          snap = { dateKey: `${k}-28`, monthKey: k, eom: true, byProduct: snap?.byProduct || {}, total: snap?.total || 0 };
        }
        added.push({ file, wb, snap });
      } catch { failed.push(`${file.name} — unreadable (re-save as .xlsx)`); }
    }
    setInvEntries((prev) => { const map = new Map(prev.map((e) => [e.file.name, e])); added.forEach((e) => map.set(e.file.name, e)); return Array.from(map.values()); });
    if (failed.length) setError((p) => [p, `Skipped ${failed.length} inventory file(s): ${failed.join('; ')}`].filter(Boolean).join(' | '));
    if (persist) await persistRefinery(added.map((e) => ({ file: e.file, kind: 'inventory', period: e.snap.monthKey || 'unknown' })));
  };

  // Folder upload: classify each spreadsheet in a picked folder and route it to the right ingester.
  // Production = a workbook with both Volume & Mass sheets; Movement = a known movement sheet name or a
  // 'category/receipts/sales' header; everything else falls to Inventory (best-effort).
  const onFolder = async (files: File[]) => {
    const xls = files.filter((f) => /\.(xlsx|xlsm|xls)$/i.test(f.name));
    const prod: File[] = [], mov: File[] = [], invF: File[] = [];
    for (const f of xls) {
      try {
        const wb = await readWb(f);
        const names = wb.SheetNames || [];
        if (names.includes('Volume') && names.includes('Mass')) { prod.push(f); continue; }
        if (names.some((n) => ['Formatted Report', 'Formatted', 'Manipulated', 'Consolidated'].includes(n))) { mov.push(f); continue; }
        const rows = XLSX.utils.sheet_to_json<any[]>(wb.Sheets[names[0]], { header: 1, blankrows: false }).slice(0, 15);
        const flat = rows.flat().map((c) => String(c || '').toLowerCase().trim());
        if (flat.some((c) => c === 'category' || c === 'receipts' || c === 'sales')) mov.push(f);
        else invF.push(f);
      } catch { /* skip unreadable */ }
    }
    if (prod.length) await onProd(prod);
    if (mov.length) await onMov(mov);
    if (invF.length) await onInv(invF);
    if (!prod.length && !mov.length && !invF.length) setError('No recognizable production / movement / inventory spreadsheets in that folder.');
  };

  // The shared hub uploader hands us the AORA-family files; route them through onFolder.
  useImperativeHandle(ref, () => ({ ingest: async (files: File[]) => { await onFolder(files); } }));

  // ---- P2b persistence (shared with the OAS tab) ----
  const [storeMsg, setStoreMsg] = useState('');
  const [rperiods, setRperiods] = useState<string[]>([]);
  const [oasPeriods, setOasPeriods] = useState<string[]>([]); // OAS months available for an OAS (adapter-sourced) dataset
  const oasReqRef = useRef(0); // guards against a stale async month-load clobbering a newer selection
  const [rmgrOpen, setRmgrOpen] = useState(false);
  const [rmgrPeriod, setRmgrPeriod] = useState('');
  const [rmgrFiles, setRmgrFiles] = useState<StoredFile[]>([]);
  const [rmgrLoading, setRmgrLoading] = useState(false);
  const [authExpired, setAuthExpired] = useState(false); // saved-uploads list came back 401 — token expired, NOT deleted data
  const [dsLoading, setDsLoading] = useState(false); // loading/parsing the selected dataset's data
  // (the dataset-driven effect below sets rperiods + restores this dataset's files on dataset change)
  async function persistRefinery(items: { file: File; kind: string; period: string }[]) {
    if (!items.length) return;
    const t = await idToken();
    const byPeriod = new Map<string, { file: File; kind: string }[]>();
    for (const it of items) { const a = byPeriod.get(it.period) || []; a.push({ file: it.file, kind: it.kind }); byPeriod.set(it.period, a); }
    let stored = 0, dups = 0;
    for (const [period, its] of Array.from(byPeriod.entries())) {
      const res = await persistUploads('refinery', dataset, period, its, t);
      stored += res.filter((r) => r.status === 'stored').length;
      dups += res.filter((r) => r.status === 'duplicate').length;
    }
    try { const last = Array.from(byPeriod.keys()).sort().reverse()[0]; if (last) localStorage.setItem(RLAST_PERIOD_KEY, last); } catch { /* storage unavailable — ignore */ }
    const parts: string[] = [];
    if (stored) parts.push(`${stored} saved`);
    if (dups) parts.push(`${dups} duplicate(s) skipped`);
    if (parts.length) setStoreMsg(parts.join(' · '));
    setRperiods(await listPeriods('refinery', dataset, t));
    oasMonthCache.current.clear();
    if (stored) onUploaded?.(); // refresh the hub's dataset list/counts (surfaces a brand-new dataset)
  }
  const openRManager = async () => {
    setRmgrOpen(true); setRmgrLoading(true);
    try {
      const t = await idToken(); const ps = await listPeriods('refinery', dataset, t); setRperiods(ps); setAuthExpired(false);
      const p = rmgrPeriod || ps[0] || ''; setRmgrPeriod(p);
      setRmgrFiles(p ? await listUploads('refinery', dataset, p, t) : []);
    } catch (e) { if (e instanceof MBAuthError) setAuthExpired(true); }
    setRmgrLoading(false);
  };
  const selectRPeriod = async (p: string) => { setRmgrPeriod(p); setRmgrLoading(true); setRmgrFiles(await listUploads('refinery', dataset, p, await idToken())); setRmgrLoading(false); };
  const removeRStored = async (sf: StoredFile) => { oasMonthCache.current.clear(); const t = await idToken(); if (await deleteUpload('refinery', dataset, sf.period, sf.uploadId, t)) { setRmgrFiles((prev) => prev.filter((x) => x.uploadId !== sf.uploadId)); setRperiods(await listPeriods('refinery', dataset, t)); } };
  // Restore a saved period's files into the view (used by the manager's "Load" button and the auto-restore effect
  // below). Routes each stored file to its kind's ingester; remembers the period as the last-worked one.
  const hydrateRPeriod = async (period: string): Promise<number> => {
    if (!period) return 0;
    const t = await idToken(); const files = await listUploads('refinery', dataset, period, t);
    if (!files.length) return 0;
    const byKind: Record<string, File[]> = {};
    for (const sf of files) { const f = await fetchStoredFile(sf); if (f) (byKind[sf.kind] = byKind[sf.kind] || []).push(f); }
    if (byKind['production']) await onProd(byKind['production'], false);
    if (byKind['movement']) await onMov(byKind['movement'], false);
    if (byKind['inventory']) await onInv(byKind['inventory'], false);
    try { localStorage.setItem(RLAST_PERIOD_KEY, period); } catch { /* ignore */ }
    return files.length;
  };
  const loadRMonth = async () => {
    if (!rmgrPeriod) return; setRmgrLoading(true);
    const n = await hydrateRPeriod(rmgrPeriod);
    setRmgrLoading(false); setRmgrOpen(false); setStoreMsg(`Loaded ${rmgrPeriod} (${n} file${n > 1 ? 's' : ''})`);
  };

  // P3d — adapt ONE stored OAS month of an OAS dataset into the Refinery balance. Volume (m³) is the
  // source. MT is DERIVED from density with a three-tier resolution validated against the real files:
  //   1) measured density by SAP grade code (OAS Material Download),
  //   2) the group's AVERAGE measured density (data-driven — e.g. Crude ≈ 0.834 from the crudes that
  //      were in tanks), for grades not in tanks this month,
  //   3) a curated per-group constant for the few minor groups with no measured density anywhere.
  // Derived MT is clearly flagged (≈ est.); volume is never derived.
  // Load one stored OAS month into balances (volume from the m3 report, measured mass from the MT report
  // when present, else density-derived) + its MTD Fuel & Loss. Shared by the period view, trends and range.
  // Per-session cache of loaded months: switching Single month <-> Range <-> Trends must not re-download 13 months
  // of workbooks. Invalidated when the dataset changes, the files list is reloaded (reloadKey), or files are
  // uploaded/deleted here.
  const oasMonthCache = useRef<Map<string, Awaited<ReturnType<typeof loadOasBalancesUncached>>>>(new Map());
  useEffect(() => { oasMonthCache.current.clear(); }, [dataset, reloadKey]);
  const loadOasBalances = async (period: string, t?: string) => {
    const ck = `${dataset}|${period}`;
    if (oasMonthCache.current.has(ck)) return oasMonthCache.current.get(ck)!;
    const bal = await loadOasBalancesUncached(period, t);
    if (bal) oasMonthCache.current.set(ck, bal);
    return bal;
  };
  const loadOasBalancesUncached = async (period: string, t?: string) => {
    const files = await listUploads('oas', dataset, period, t);
    const cands: { yd: YieldData; uploadedAt?: string }[] = []; const invRows: InvRow[] = [];
    let fuelLoss: FuelLossReport | null = null;
    // Download the month's files CONCURRENTLY. A period holds three (m3 yields, MT yields, material)
    // totalling 3.5-5.3 MB on EET, and fetching them one after another made the wait the SUM of three
    // round trips. Parsing still runs in file order, so the result is byte-identical.
    const fetched = await Promise.all(files.map(async (sf) => ({ sf, f: await fetchStoredFile(sf) })));
    for (const { sf, f } of fetched) {
      if (!f) continue;
      try {
        const wb = XLSX.read(await f.arrayBuffer(), { type: 'array', cellDates: true });
        const res = classifyOas(wb);
        if (res.kind === 'yields' && res.yields) { cands.push({ yd: res.yields, uploadedAt: sf.uploaded_at }); if (!fuelLoss) { try { fuelLoss = parseFuelLoss(wb, period, 'oas'); } catch { /* no MTD sheet */ } } }
        else if (res.kind === 'material' && res.inv) invRows.push(...res.inv);
      } catch { /* skip unreadable */ }
    }
    const { vol, mass } = pickYieldsByBasis(cands);
    const yd = vol || mass;
    if (!yd) return null;
    const b = buildOasBalances(yd, invRows, vol ? mass : null);
    if (!b.balV.length) return null;
    return { ...b, fuelLoss, volumeBasis: vol ? 'm3' : 'MT' as 'm3' | 'MT' };
  };

  const loadOasMonth = async (period: string, tok?: string): Promise<boolean> => {
    const req = ++oasReqRef.current; // claim the latest-request slot; a later click supersedes this one
    setProgress({ label: `Loading ${keyLabel(period)}…`, done: 0, total: 1 });
    try { return await loadOasMonthInner(period, req, tok); } finally { if (oasReqRef.current === req) setProgress(null); }
  };
  // Rebuild the month's two balances from the stored analytics snapshot. The snapshot holds the
  // identical per-grade rows (verified against the workbook path across five periods on both bases:
  // same row counts, worst absolute difference 0.0), at ~45 KB instead of 3.5-5.3 MB of xlsx.
  // `theoretical` and `active` are derived rather than stored -- computeBalance defines
  // gainLoss = theoretical - closing, so theoretical = closing + gainLoss -- and `category` is
  // re-derived with computeBalance's own rule.
  const rowFromSnapshot = (product: string, g: any): BalanceRow => {
    const n = (v: any) => (typeof v === 'number' ? v : 0);
    const prod = n(g.production);
    const category: BalanceRow['category'] = /(CRUDE|DILBIT|BITUMEN)/.test(product.toUpperCase()) ? 'crude'
      : prod < -1 ? 'crude' : prod > 1 ? 'product' : (n(g.receipts) > n(g.shipments) ? 'crude' : 'product');
    return {
      product, opening: n(g.opening), receipts: n(g.receipts), production: prod,
      shipments: n(g.shipments), reclass: n(g.reclass), theoretical: n(g.closing) + n(g.gainLoss),
      closing: n(g.closing), gainLoss: n(g.gainLoss), category,
      active: [g.opening, g.closing, g.receipts, g.shipments, g.reclass, g.production]
        .some((v: any) => Math.abs(n(v)) > 0.5),
    };
  };
  const monthFromSnapshot = (snap: any) => {
    const grades: any[] = snap.grades || [];
    // A grade with no measurement on a basis is absent from that basis, exactly as the workbook
    // balance has it: volume-only grades carry mass=null, mass-only grades carry null volume legs.
    const balV = grades.filter((g) => g.production !== null && g.production !== undefined)
      .map((g) => rowFromSnapshot(g.product, g));
    const balM = grades.filter((g) => g.mass).map((g) => rowFromSnapshot(g.product, g.mass));
    const oasAssignments: Record<string, string> = {};
    grades.forEach((g) => { if (g.product && g.category) oasAssignments[g.product] = g.category; });
    const meta = snap.meta || {}; const adj = (snap.balance || {}).adjustment || {};
    return {
      balV, balM: balM.length ? balM : null, oasAssignments,
      coverageVol: typeof meta.massCoverage === 'number' ? meta.massCoverage : 1,
      oasAdjustment: adj.volume ?? null, oasAdjustmentMass: adj.mass ?? null,
      massMeasured: !!meta.massMeasured, fuelLoss: snap.fuelLoss ?? null,
      volumeBasis: (meta.volumeBasis === 'MT' ? 'MT' : 'm3') as 'm3' | 'MT',
    };
  };

  const loadOasMonthInner = async (period: string, req: number, tok?: string): Promise<boolean> => {
    const t = tok || await idToken();
    // Preferred path: the stored snapshot. Falls through to re-parsing the workbooks when a period
    // has no snapshot yet (never computed, or a dataset whose compute has not run).
    try {
      const snap = await fetchPeriodSnapshot(dataset, period, 'refinery', t);
      if (snap) {
        const b = monthFromSnapshot(snap);
        if (b.balV.length) {
          if (oasReqRef.current !== req) return false;   // a newer month was clicked mid-load
          setBasis('volume');
          setResult({ balV: b.balV, balM: b.balM, mov: null, prodTot: null, key: period,
            label: `${dataset} · ${period}`, measured: null, prevBalV: null, prevBalM: null, prevKey: null,
            oas: true, oasDerivedMass: !!b.balM && !b.massMeasured, oasMassMeasured: b.massMeasured,
            oasVolumeBasis: b.volumeBasis, oasMassCoverage: b.coverageVol,
            oasAssignments: b.oasAssignments, oasAdjustment: b.oasAdjustment,
            oasAdjustmentMass: b.oasAdjustmentMass, fuelLoss: b.fuelLoss });
          return true;
        }
      }
    } catch (e) { if (e instanceof MBAuthError) throw e; }
    const files = await listUploads('oas', dataset, period, t);
    // A month may hold the m3 report ("Daily Yields"), the MT report ("DAILY OAS MB"), or both -- the SAME
    // per-grade balance in two units. Volume comes from the m3 file; measured mass from the MT file (no
    // density estimate). Previously the last-parsed file silently won, so an MT workbook could be read
    // as m3 with an "estimated mass" derived on top of tonnes.
    const yieldsCands: { yd: YieldData; uploadedAt?: string }[] = [];
    let fuelLoss: FuelLossReport | null = null;   // the "MTD" master-sheet Fuel & Loss (own fuel, flare, BS&W, unaccounted loss)
    const invRows: InvRow[] = [];
    for (const sf of files) {
      const f = await fetchStoredFile(sf); if (!f) continue;
      try {
        const wb = XLSX.read(await f.arrayBuffer(), { type: 'array', cellDates: true });
        const res = classifyOas(wb);
        if (res.kind === 'yields' && res.yields) { yieldsCands.push({ yd: res.yields, uploadedAt: sf.uploaded_at }); if (!fuelLoss) { try { fuelLoss = parseFuelLoss(wb, period, 'oas'); } catch { /* no MTD sheet in this variant */ } } }
        else if (res.kind === 'material' && res.inv) invRows.push(...res.inv);
      } catch { /* skip */ }
    }
    const { vol, mass } = pickYieldsByBasis(yieldsCands);
    const yd = vol || mass;
    if (!yd) return false;
    const b = buildOasBalances(yd, invRows, vol ? mass : null);
    const { balV, balM, coverageVol, oasAssignments, oasAdjustment, oasAdjustmentMass, massMeasured } = b;
    if (!balV.length) return false;
    if (oasReqRef.current !== req) return false; // a newer month was requested mid-load — drop this stale result
    setBasis('volume');
    setResult({ balV, balM, mov: null, prodTot: null, key: period, label: `${dataset} · ${period}`, measured: null, prevBalV: null, prevBalM: null, prevKey: null, oas: true, oasDerivedMass: !!balM && !massMeasured, oasMassMeasured: massMeasured, oasVolumeBasis: vol ? 'm3' : 'MT', oasMassCoverage: coverageVol, oasAssignments, oasAdjustment, oasAdjustmentMass, fuelLoss });
    return true;
  };

  // Load the ACTIVE DATASET: whenever `dataset` changes (or on mount), clear the view and restore
  // ALL of that dataset's saved months. Switching datasets in the hub re-renders this tab from the
  // newly-selected customer's data. (Tab switches don't remount — the hub toggles visibility — so
  // this only fires on a real dataset change or a fresh mount.)
  useEffect(() => {
    if (!dataset) return;
    let cancelled = false;
    setDsLoading(true);
    (async () => {
      try {
        const t = await idToken();
        const ps = await listPeriods('refinery', dataset, t);
        if (cancelled) return;
        setAuthExpired(false); setRperiods(ps);
        setProdEntries([]); setMovEntries([]); setInvEntries([]); setResult(null); setOasPeriods([]);
        // Restore ALL saved months (Refinery natively spans months).
        const byKind: Record<string, File[]> = {};
        for (const p of ps) {
          const files = await listUploads('refinery', dataset, p, t);
          for (const sf of files) { const f = await fetchStoredFile(sf); if (f) (byKind[sf.kind] = byKind[sf.kind] || []).push(f); }
        }
        if (cancelled) return;
        if (byKind['production']) {
          await onProd(byKind['production'], false);
        } else {
          // OAS-sourced dataset has no AORA production. List every OAS month as a selectable
          // period and adapt the most recent one by default (the others are one click away).
          const oasPs = await listPeriods('oas', dataset, t);
          if (!cancelled && oasPs.length) {
            const ordered = [...oasPs].sort();  // chronological, oldest → newest
            setOasPeriods(ordered);
            // Default to the newest REAL month — a stray non-month key (e.g. 'unknown') sorts last and must not win.
            const months = ordered.filter((p) => /^\d{4}-\d{2}$/.test(p));
            await loadOasMonth((months.length ? months : ordered)[(months.length ? months : ordered).length - 1], t);
          } else {
            setOasPeriods([]);
          }
        }
        if (byKind['movement']) await onMov(byKind['movement'], false);
        if (byKind['inventory']) await onInv(byKind['inventory'], false);
      } catch (e) { if (e instanceof MBAuthError) setAuthExpired(true); }
      finally { if (!cancelled) setDsLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset, reloadKey]);

  // match each production period to the most-specific movement file that covers it,
  // plus (optional) bracketing EOM inventory snapshots (prior-month = opening, this-month = closing)
  const invByMonth = useMemo(() => { const m = new Map<string, InvEntry>(); invEntries.filter((e) => e.snap.eom).forEach((e) => m.set(e.snap.monthKey, e)); return m; }, [invEntries]);
  const periods: PeriodMatch[] = useMemo(() => prodEntries.map((p) => {
    const mov = movEntries.filter((m) => m.months.has(p.key)).sort((a, b) => a.months.size - b.months.size)[0] || null;
    return {
      key: p.key, label: keyLabel(p.key), prod: p, mov, ready: Boolean(mov),
      openInv: invByMonth.get(prevMonthKey(p.key)) || null, closeInv: invByMonth.get(p.key) || null,
    };
  }).sort((a, b) => a.key.localeCompare(b.key)), [prodEntries, movEntries, invByMonth]);
  const readyCount = periods.filter((p) => p.ready).length;

  const genPeriod = (p: PeriodMatch) => {
    if (!p.ready || !p.mov) return;
    setLoading(true); setError(null);
    try {
      const streamsV = parseProductionTab(p.prod.wb.Sheets['Volume']);
      const streamsM = parseProductionTab(p.prod.wb.Sheets['Mass']);
      if (streamsV.length === 0) throw new Error('No product rows found in the production report Volume tab.');
      const balV = computeBalance(streamsV);
      const balM = computeBalance(streamsM);
      const [y, m] = p.key.split('-').map(Number);
      // Aggregate all rows only when the file has NO parseable dates; otherwise date-filter
      // to the target month (handles files that mix months despite a single-month name).
      const wholeFile = p.mov.internalMonths.size === 0;
      const mov = parseMovements(p.mov.wb, y, m, wholeFile);
      const prodTot = streamsV.reduce((a, s) => { a.rec += s.rec; a.ship += s.ship; return a; }, { rec: 0, ship: 0 });
      // Measured reconciliation (refinery-total, bbls) — only when we have both bracketing
      // tank-gauging snapshots. Uses independently-measured inventory instead of the AORA
      // reconciled numbers, so the residual is a *real* gain/loss (name-agnostic, total level).
      let measured: any = null;
      if (p.openInv && p.closeInv) {
        const invOpen = p.openInv.snap.total, invClose = p.closeInv.snap.total;
        const production = balV.reduce((a, r) => a + r.production, 0);
        const reclass = balV.reduce((a, r) => a + r.reclass, 0);
        const theoreticalClose = invOpen + mov.totals.Receipts + production - mov.totals.Sales - mov.totals.Consumptions - reclass;
        measured = {
          openDate: p.openInv.snap.dateKey, closeDate: p.closeInv.snap.dateKey,
          invOpen, invClose, production, reclass,
          receipts: mov.totals.Receipts, sales: mov.totals.Sales, consumption: mov.totals.Consumptions,
          theoreticalClose, gainLoss: theoreticalClose - invClose,
        };
      }
      // prior available month, for the month-over-month comparison in the yield panel
      const prevP = periods.filter((x) => x.ready && x.key < p.key).sort((a, z) => z.key.localeCompare(a.key))[0];
      let prevBalV: BalanceRow[] | null = null, prevBalM: BalanceRow[] | null = null, prevKey: string | null = null;
      if (prevP) {
        prevBalV = computeBalance(parseProductionTab(prevP.prod.wb.Sheets['Volume']));
        prevBalM = computeBalance(parseProductionTab(prevP.prod.wb.Sheets['Mass']));
        prevKey = prevP.key;
      }
      // The production report's own "Loss/Gain" line (Total Charges − Total Yields) — the real refinery
      // loss, which the stock reconciliation above closes to ~0.
      let fuelLoss: FuelLossReport | null = null;
      try { fuelLoss = parseFuelLoss(p.prod.wb, p.key, 'aora'); } catch { /* no Volume/Mass Loss-Gain line */ }
      setResult({ balV, balM, mov, prodTot, key: p.key, measured, prevBalV, prevBalM, prevKey, fuelLoss });
    } catch (e: any) { setError(e?.message || 'Failed to generate the mass balance.'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    if (!dataset) { setLossSeries(null); return; }
    (async () => {
      try {
        const t = await idToken();
        const ls = await fetchLossSeries(dataset, 'refinery', t);
        if (!cancelled) setLossSeries(ls);
        const ms = await fetchMbSettings(dataset, t);
        if (!cancelled) setMbSettings(ms);
      } catch { if (!cancelled) setLossSeries(null); }   // decoration only: never block trends
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset]);

  // ---------- trends (time views + M2M analytics) ----------
  const trendPoint = (bal: BalanceRow[], key: string) => {
    const t = bal.reduce((a, r) => ({
      opening: a.opening + r.opening, receipts: a.receipts + r.receipts, production: a.production + r.production,
      shipments: a.shipments + r.shipments, reclass: a.reclass + r.reclass, gainLoss: a.gainLoss + r.gainLoss,
    }), { opening: 0, receipts: 0, production: 0, shipments: 0, reclass: 0, gainLoss: 0 });
    const byProduct: Record<string, any> = {};
    bal.forEach((r) => { byProduct[r.product] = { production: r.production, gainLoss: r.gainLoss, receipts: r.receipts, shipments: r.shipments, category: r.category }; });
    return { key, ...t, byProduct };
  };
  const genTrends = async (b: 'volume' | 'mass' = basis) => {
    const ready = periods.filter((p) => p.ready);
    setLoading(true); setError(null);
    try {
      // Preferred path: ONE request over the stored analytics snapshots. The client-side paths below
      // rebuild the same balance by downloading and re-parsing every month's workbook -- 39 downloads
      // and 51.9 MB on EET -- and they also feed netProduction a BalanceRow category ('crude' |
      // 'product'), which never matches the 'feedstock' prefix it is tested against. The snapshot
      // carries the real product-mapping category, so the headline is right as well as faster.
      // Falls through to the original paths when the dataset has no snapshots.
      try {
        const srv = await fetchTrendSeries(dataset, b, 'refinery', await idToken());
        if (srv) { setTrends(srv.series.map((pt) => ({ ...pt }))); return; }
      } catch (e) { if (e instanceof MBAuthError) throw e; }   // any other failure: use the fallback
      if (ready.length) {
        // Refinery (AORA) path — re-parse each month's production sheet.
        const series = ready.map((p) => trendPoint(computeBalance(parseProductionTab(p.prod.wb.Sheets[b === 'mass' ? 'Mass' : 'Volume'])), p.key))
          .sort((a, b2) => a.key.localeCompare(b2.key));
        setTrends(series);
      } else {
        // OAS (OAS) path — adapt each stored OAS month's Daily Yields into a balance.
        const t = await idToken();
        const oasPs = await listPeriods('oas', dataset, t);
        if (!oasPs.length) { setError('No months to trend for this dataset.'); return; }
        const norm = (s: string) => String(s || '').trim().toUpperCase();
        const series: any[] = [];
        const sortedPs = [...oasPs].sort();
        for (let i = 0; i < sortedPs.length; i++) { const per = sortedPs[i];
          setProgress({ label: `Building trends — loading ${keyLabel(per)}`, done: i, total: sortedPs.length });
          const bal = await loadOasBalances(per, t);
          if (!bal) continue;
          const rows = b === 'mass' ? bal.balM : bal.balV;
          if (!rows || !rows.length) continue;
          series.push(trendPoint(rows, per));
        }
        if (!series.length) { setError('No months to trend for this dataset.'); return; }
        setTrends(series.sort((a, b2) => a.key.localeCompare(b2.key)));
      }
    } catch (e: any) { setError(e?.message || 'Failed to build trends.'); }
    finally { setLoading(false); setProgress(null); }
  };
  // Continuous date coverage across ALL uploaded data (production months + movement months).
  const coverage = useMemo(() => {
    const months = new Set<string>();
    periods.forEach((p) => months.add(p.key));
    movEntries.forEach((e) => e.months.forEach((m) => months.add(m)));
    oasPeriods.forEach((m) => { if (/^\d{4}-\d{2}$/.test(m)) months.add(m); });   // OAS-sourced datasets (EET)
    if (!months.size) return null;
    const sorted = Array.from(months).sort();
    const minM = sorted[0], maxM = sorted[sorted.length - 1];
    const [my, mm] = maxM.split('-').map(Number);
    const to = `${maxM}-${String(new Date(my, mm, 0).getDate()).padStart(2, '0')}`;
    const gaps: string[] = [];
    let [y, m] = minM.split('-').map(Number);
    while (`${y}-${String(m).padStart(2, '0')}` <= maxM) {
      const k = `${y}-${String(m).padStart(2, '0')}`;
      if (!months.has(k)) gaps.push(k);
      m++; if (m > 12) { m = 1; y++; }
    }
    return { from: `${minM}-01`, to, minM, maxM, gaps };
  }, [periods, movEntries, oasPeriods]);
  const effFrom = fromDate || coverage?.from || '';
  const effTo = toDate || coverage?.to || '';
  const setQuickRange = (months: number) => {
    if (!coverage) return;
    if (months === 0) { setFromDate(''); setToDate(''); return; }   // All
    const [y, m, d] = coverage.to.split('-').map(Number);
    const dt = new Date(y, m - 1, d); dt.setDate(1); dt.setMonth(dt.getMonth() - (months - 1));
    setFromDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`);
    setToDate(coverage.to);
  };

  // Combined grouped balance for a continuous date range: chain opening (first month) →
  // closing (last month), sum the flows across every in-range month.
  // A month can list the same product name under two SAP codes (e.g. CommDieselUKBaseProd). Merge them first so
  // chaining by name keeps every row's stock and flows.
  const mergeSameProduct = (rows: BalanceRow[]): BalanceRow[] => {
    const acc = new Map<string, BalanceRow>();
    rows.forEach((r) => {
      const a = acc.get(r.product);
      if (!a) { acc.set(r.product, { ...r }); return; }
      a.opening += r.opening; a.closing += r.closing; a.receipts += r.receipts; a.production += r.production; a.shipments += r.shipments; a.reclass += r.reclass;
      a.theoretical = a.opening + a.receipts - a.shipments - a.reclass + a.production; a.gainLoss = a.theoretical - a.closing; a.active = a.active || r.active;
    });
    return Array.from(acc.values());
  };
  // Sites restate opening stock between months (re-gauging, reclassification). Sum of (opening[n+1] - closing[n])
  // per product across consecutive months: the part of a multi-month residual that is NOT month-internal loss.
  const monthBoundaryRestatement = (monthly: BalanceRow[][]): number => {
    let total = 0;
    for (let i = 1; i < monthly.length; i++) {
      const prev = new Map(monthly[i - 1].map((r) => [r.product, r.closing]));
      monthly[i].forEach((r) => { if (prev.has(r.product)) total += r.opening - (prev.get(r.product) as number); });
    }
    return total;
  };
  // Chain a list of monthly balances into one: opening of the first month, closing of the last, flows summed.
  const chainBalances = (monthlyRaw: BalanceRow[][]): BalanceRow[] => {
    const monthly = monthlyRaw.map(mergeSameProduct);
    const acc = new Map<string, any>(); const seen = new Set<string>();
    monthly.forEach((rows) => rows.forEach((r) => {
      const a = acc.get(r.product) || { product: r.product, opening: 0, closing: 0, rec: 0, prod: 0, ship: 0, recl: 0, category: r.category };
      if (!seen.has(r.product)) { a.opening = r.opening; seen.add(r.product); }
      a.closing = r.closing; a.rec += r.receipts; a.prod += r.production; a.ship += r.shipments; a.recl += r.reclass;
      acc.set(r.product, a);
    }));
    return Array.from(acc.values()).map((a) => {
      const theoretical = a.opening + a.rec - a.ship - a.recl + a.prod;
      const active = [a.opening, a.closing, a.rec, a.ship, a.recl, a.prod].some((v) => Math.abs(v) > 0.5);
      return { product: a.product, opening: a.opening, receipts: a.rec, production: a.prod, shipments: a.ship, reclass: a.recl, theoretical, closing: a.closing, gainLoss: theoretical - a.closing, active, category: a.category } as BalanceRow;
    });
  };
  const monthGaps = (keys: string[]) => {
    const set = new Set(keys); const gaps: string[] = []; let [gy, gm] = keys[0].split('-').map(Number); const lastK = keys[keys.length - 1];
    while (`${gy}-${String(gm).padStart(2, '0')}` <= lastK) { const k = `${gy}-${String(gm).padStart(2, '0')}`; if (!set.has(k)) gaps.push(k); gm++; if (gm > 12) { gm = 1; gy++; } }
    return gaps;
  };

  // OAS-sourced dataset (EET): combine the stored OAS months in the range.
  const genRangeOas = async (from7: string, to7: string) => {
    const keys = oasPeriods.filter((k) => /^\d{4}-\d{2}$/.test(k) && k >= from7 && k <= to7).sort();
    if (!keys.length) { setError('No saved months in the selected range.'); return; }
    setLoading(true); setError(null);
    try {
      const t = await idToken();
      const months: { key: string; bal: NonNullable<Awaited<ReturnType<typeof loadOasBalances>>> }[] = [];
      for (let i = 0; i < keys.length; i++) { const k = keys[i];
        setProgress({ label: `Combining ${keys.length} months — loading ${keyLabel(k)}`, done: i, total: keys.length });
        const bal = await loadOasBalances(k, t); if (bal) months.push({ key: k, bal });
      }
      setProgress({ label: 'Chaining months into one balance…', done: keys.length, total: keys.length });
      if (!months.length) { setError('None of the months in the range could be read.'); return; }
      const balV = chainBalances(months.map((m) => m.bal.balV));
      const allMass = months.every((m) => m.bal.balM && m.bal.balM.length);
      const balM = allMass ? chainBalances(months.map((m) => m.bal.balM as BalanceRow[])) : null;
      const restatementV = monthBoundaryRestatement(months.map((m) => mergeSameProduct(m.bal.balV)));
      const restatementM = allMass ? monthBoundaryRestatement(months.map((m) => mergeSameProduct(m.bal.balM as BalanceRow[]))) : null;
      const massMeasured = allMass && months.every((m) => m.bal.massMeasured);
      // Fuel & Loss across the range: sum the MTD figures; percentages recomputed on the summed crude.
      const fls = months.map((m) => m.bal.fuelLoss).filter((f): f is FuelLossReport => !!f);
      let fuelLoss: FuelLossReport | null = null;
      if (fls.length) {
        const sum = (k: keyof FuelLossReport) => fls.reduce((a, f) => a + (Number(f[k]) || 0), 0);
        const crude = sum('crudeProcessed'), loss = sum('unaccountedLoss'), fuel = sum('totalFuel');
        fuelLoss = { ...fls[fls.length - 1], period: `${keys[0]} → ${keys[keys.length - 1]}`, crudeProcessed: crude, naturalGas: sum('naturalGas'), ownFuel: sum('ownFuel'), totalFuel: fuel,
          unaccountedLoss: loss, flare: sum('flare'), bsw: sum('bsw'), fuelPct: crude ? fuel / crude : null, lossPct: crude ? loss / crude : null, fnlPct: crude ? (fuel + loss) / crude : null,
          feed: [], products: [], subBalances: [] } as FuelLossReport;
      }
      const monthKeys = months.map((m) => m.key); const label = `${monthKeys[0]} → ${monthKeys[monthKeys.length - 1]}`;
      const oasAssignments = Object.assign({}, ...months.map((m) => m.bal.oasAssignments));
      setBasis('volume');
      setResult({ balV, balM, mov: null, prodTot: null, key: label.replace(/[^0-9a-z]+/gi, '_'), label: `${dataset} · ${label}`, isRange: true, rangeGaps: monthGaps(monthKeys), measured: null,
        prevBalV: null, prevBalM: null, prevKey: null, oas: true, oasDerivedMass: !!balM && !massMeasured, oasMassMeasured: massMeasured, oasAssignments, fuelLoss,
        oasAdjustment: months.reduce((a, m) => a + (m.bal.oasAdjustment || 0), 0), oasAdjustmentMass: allMass ? months.reduce((a, m) => a + (m.bal.oasAdjustmentMass || 0), 0) : null,
        rangeRestatement: { volume: restatementV, mass: restatementM } });
    } catch (e: any) { setError(e?.message || 'Failed to build the range balance.'); }
    finally { setLoading(false); setProgress(null); }
  };

  const genRange = () => {
    const from7 = effFrom.slice(0, 7), to7 = effTo.slice(0, 7);
    if (!periods.length && oasPeriods.length) { void genRangeOas(from7, to7); return; }
    const inP = periods.filter((p) => p.ready && p.key >= from7 && p.key <= to7).sort((a, z) => a.key.localeCompare(z.key));
    if (!inP.length) { setError('No generatable months in the selected range.'); return; }
    setLoading(true); setError(null);
    try {
      const aggBase = (sheet: 'Volume' | 'Mass'): BalanceRow[] => {
        const acc = new Map<string, any>(); const seen = new Set<string>();
        inP.forEach((p) => {
          computeBalance(parseProductionTab(p.prod.wb.Sheets[sheet])).forEach((r) => {
            const a = acc.get(r.product) || { product: r.product, opening: 0, closing: 0, rec: 0, prod: 0, ship: 0, recl: 0, category: r.category };
            if (!seen.has(r.product)) { a.opening = r.opening; seen.add(r.product); }  // first month opening
            a.closing = r.closing;                                                      // last month closing
            a.rec += r.receipts; a.prod += r.production; a.ship += r.shipments; a.recl += r.reclass;
            acc.set(r.product, a);
          });
        });
        return Array.from(acc.values()).map((a) => {
          const theoretical = a.opening + a.rec - a.ship - a.recl + a.prod;
          const active = [a.opening, a.closing, a.rec, a.ship, a.recl, a.prod].some((v) => Math.abs(v) > 0.5);
          return { product: a.product, opening: a.opening, receipts: a.rec, production: a.prod, shipments: a.ship, reclass: a.recl, theoretical, closing: a.closing, gainLoss: theoretical - a.closing, category: a.category, active };
        });
      };
      const balV = aggBase('Volume'); const balM = aggBase('Mass');
      const movTot = { Receipts: 0, Sales: 0, Consumptions: 0 };
      inP.forEach((p) => { if (!p.mov) return; const [y, m] = p.key.split('-').map(Number); const mv = parseMovements(p.mov.wb, y, m, p.mov.internalMonths.size === 0); movTot.Receipts += mv.totals.Receipts; movTot.Sales += mv.totals.Sales; movTot.Consumptions += mv.totals.Consumptions; });
      const prodTot = balV.reduce((a, r) => { a.rec += r.receipts; a.ship += r.shipments; return a; }, { rec: 0, ship: 0 });
      const monthsSel = inP.map((p) => p.key); const setMonths = new Set(monthsSel);
      const gaps: string[] = []; let [gy, gm] = monthsSel[0].split('-').map(Number); const lastK = monthsSel[monthsSel.length - 1];
      while (`${gy}-${String(gm).padStart(2, '0')}` <= lastK) { const k = `${gy}-${String(gm).padStart(2, '0')}`; if (!setMonths.has(k)) gaps.push(k); gm++; if (gm > 12) { gm = 1; gy++; } }
      const label = `${monthsSel[0]} → ${monthsSel[monthsSel.length - 1]}`;
      setResult({ balV, balM, mov: { totals: movTot, months: new Set(monthsSel), byCode: {} }, prodTot, key: label.replace(/[^0-9a-z]+/gi, '_'), label, isRange: true, rangeGaps: gaps, measured: null });
    } catch (e: any) { setError(e?.message || 'Failed to build the range balance.'); }
    finally { setLoading(false); }
  };

  // Analytics run over the selected continuous date range, not per uploaded file.
  const buckets = useMemo(() => {
    const inRange = (mk: string) => mk >= effFrom.slice(0, 7) && mk <= effTo.slice(0, 7);
    const lossBy = new Map((lossSeries?.periods || []).map((x) => [x.period, x]));
    const withLoss = (trends || []).filter((s) => inRange(s.key)).map((s) => {
      const lp = lossBy.get(s.key);
      const crude = lp?.crude_processed ?? null;
      // Some months report a loss PERCENTAGE but no absolute figure (EET 2025-10 and 2025-12 both
      // store unaccountedLoss: null). Derive the amount so those periods still aggregate.
      const amt = lp?.unaccounted_loss ?? (lp?.loss_pct != null && crude != null ? (lp.loss_pct / 100) * crude : null);
      return (amt != null && crude) ? { ...s, realLoss: amt, crudeProc: crude } : s;
    });
    return aggregateSeries(withLoss, gran);
  }, [trends, gran, effFrom, effTo, lossSeries]);
  const macro = useMemo(() => {
    if (!buckets.length) return null;
    const tot = buckets.reduce((a, b) => ({
      production: a.production + b.production, netProduction: a.netProduction + b.netProduction,
      gainLoss: a.gainLoss + b.gainLoss, throughput: a.throughput + b.throughput,
      realLoss: a.realLoss + (b.realLoss || 0), crudeProc: a.crudeProc + (b.crudeProc || 0),
    }), { production: 0, netProduction: 0, gainLoss: 0, throughput: 0, realLoss: 0, crudeProc: 0 });
    const hasRealLoss = tot.crudeProc > 0;
    const real = buckets.filter((b) => b.hasRealLoss);
    return {
      tot, n: buckets.length, first: buckets[0].label, last: buckets[buckets.length - 1].label,
      hasRealLoss,
      // Weighted over crude processed, not an average of percentages.
      avgClosure: real.length ? real.reduce((a, b) => a + b.closurePct, 0) / real.length
                              : buckets.reduce((a, b) => a + b.closurePct, 0) / buckets.length,
      overallClosure: hasRealLoss ? (tot.realLoss / tot.crudeProc) * 100
                                  : (tot.throughput ? (tot.gainLoss / tot.throughput) * 100 : 0),
      ledgerResidual: tot.gainLoss,
    };
  }, [buckets]);
  const movers = useMemo(() => {
    if (!buckets.length) return [] as any[];
    const cur = buckets[buckets.length - 1], prev = buckets.length > 1 ? buckets[buckets.length - 2] : null;
    const names = new Set<string>([...Object.keys(cur.byProduct), ...(prev ? Object.keys(prev.byProduct) : [])]);
    return Array.from(names).map((n) => {
      const c = cur.byProduct[n] || { production: 0, category: 'product' }; const p = prev ? prev.byProduct[n] : null;
      const cv = c.production, pv = p ? p.production : 0, d = cv - pv;
      return { product: n, category: c.category, cur: cv, prev: pv, delta: d, pct: pv ? (d / Math.abs(pv)) * 100 : 0 };
    }).filter((r) => Math.abs(r.cur) > 0.5 || Math.abs(r.prev) > 0.5).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  }, [buckets]);
  const drillSeries = useMemo(() => (!drill ? [] : buckets.map((b) => ({ label: b.label, production: b.byProduct[drill]?.production || 0, gainLoss: b.byProduct[drill]?.gainLoss || 0 }))), [buckets, drill]);

  // Day / Week movement buckets (dated movement rows) — defined here so the chart config can use them
  const isDaily = gran === 'day' || gran === 'week';
  const movDaily = useMemo(() => {
    if (!isDaily) return [] as any[];
    const monthsCovered = new Set<string>(); movEntries.forEach((e) => e.months.forEach((mk) => monthsCovered.add(mk)));
    const byEntry = new Map<MovEntry, Set<string>>();
    monthsCovered.forEach((mk) => {
      const e = movEntries.filter((x) => x.months.has(mk)).sort((a, b) => a.months.size - b.months.size)[0];
      if (e) { if (!byEntry.has(e)) byEntry.set(e, new Set()); byEntry.get(e)!.add(mk); }
    });
    const rows: MovRow[] = [];
    byEntry.forEach((mset, e) => { parseMovementRowsDated(e.wb, basis).forEach((r) => { if (mset.has(r.dateKey.slice(0, 7)) && r.dateKey >= effFrom && r.dateKey <= effTo) rows.push(r); }); });
    const bucketKey = (dateKey: string) => {
      if (gran === 'day') return dateKey;
      const [y, m, d] = dateKey.split('-').map(Number);
      const dt = new Date(y, m - 1, d); dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7)); // Monday of the week
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };
    const map = new Map<string, any>();
    rows.forEach((r) => {
      const bk = bucketKey(r.dateKey);
      const b = map.get(bk) || { label: gran === 'week' ? `wk ${bk}` : bk, key: bk, receipts: 0, sales: 0, consumption: 0, byProduct: {} as Record<string, any> };
      const pv = b.byProduct[r.desc || r.code] || { receipts: 0, sales: 0, consumption: 0, net: 0 };
      if (r.category === 'Receipts') { b.receipts += r.vol; pv.receipts += r.vol; }
      else if (r.category === 'Sales') { b.sales += r.vol; pv.sales += r.vol; }
      else { b.consumption += r.vol; pv.consumption += r.vol; }
      pv.net = pv.receipts - pv.sales - pv.consumption;
      b.byProduct[r.desc || r.code] = pv; map.set(bk, b);
    });
    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [isDaily, gran, movEntries, basis, effFrom, effTo]);

  // Chart source: production buckets (month+) or movement buckets (day/week). valueOf = the per-product metric.
  const chartSrc = isDaily ? movDaily : buckets;
  const chartValueOf = useCallback((pv: any) => (isDaily ? (pv.receipts + pv.sales + pv.consumption) : pv.production), [isDaily]);

  // Options for the single-group / single-product chart selectors (present in the range, by size)
  const PALETTE = ['#4a7fbf', '#e07a1f', '#2e7d32', '#c0392b', '#8e44ad', '#16a085', '#a0672c', '#d4a017', '#7f8c8d', '#2980b9', '#c0603c', '#27ae60', '#8e6f3e', '#e67e22', '#1abc9c', '#9b59b6'];
  const subOfProduct = useCallback((product: string) => (mapping?.subCategories || SUBCATS).find((s) => s.id === (mapping?.assignments[product] || oasAssignments?.[product] || heuristicSubCat(product) || '')), [mapping, oasAssignments]);
  const groupLabelOf = useCallback((subId: string) => { const s = (mapping?.subCategories || SUBCATS).find((x) => x.id === subId); return s ? `${catLabel(s.category)} · ${s.label}` : subId; }, [mapping]);
  const breakdownOptions = useMemo(() => {
    const subs = mapping?.subCategories || SUBCATS; const cats = mapping?.categories || CATEGORIES;
    const gTot = new Map<string, number>(); const pTot = new Map<string, number>();
    chartSrc.forEach((b: any) => Object.entries(b.byProduct).forEach(([prod, pv]: any) => {
      const val = Math.abs(chartValueOf(pv)); if (val < 0.5) return;
      const gid = subOfProduct(prod)?.id || 'unassigned';
      gTot.set(gid, (gTot.get(gid) || 0) + val);
      pTot.set(prod, (pTot.get(prod) || 0) + val);
    }));
    const gOrder: string[] = []; cats.forEach((c) => subs.filter((s) => s.category === c.id).forEach((s) => gOrder.push(s.id)));
    const groups = Array.from(gTot.keys()).sort((a, b) => (gOrder.indexOf(a) < 0 ? 999 : gOrder.indexOf(a)) - (gOrder.indexOf(b) < 0 ? 999 : gOrder.indexOf(b))).map((id) => ({ id, label: groupLabelOf(id) }));
    const products = Array.from(pTot.keys()).sort((a, b) => (pTot.get(b) || 0) - (pTot.get(a) || 0));
    return { groups, products };
  }, [chartSrc, mapping, subOfProduct, groupLabelOf, chartValueOf]);
  const effGroup = useMemo(() => (breakdownOptions.groups.some((g) => g.id === chartSel) ? chartSel : (breakdownOptions.groups[0]?.id || '')), [breakdownOptions, chartSel]);
  const productsInGroup = useMemo(() => {
    const tot = new Map<string, number>();
    chartSrc.forEach((b: any) => Object.entries(b.byProduct).forEach(([prod, pv]: any) => {
      const val = Math.abs(chartValueOf(pv)); if (val < 0.5) return;
      if (subOfProduct(prod)?.id === effGroup) tot.set(prod, (tot.get(prod) || 0) + val);
    }));
    return Array.from(tot.keys()).sort((a, b) => (tot.get(b) || 0) - (tot.get(a) || 0));
  }, [chartSrc, effGroup, subOfProduct, chartValueOf]);
  const effProd = useMemo(() => (chartProd === 'ALL' || productsInGroup.includes(chartProd) ? chartProd : 'ALL'), [chartProd, productsInGroup]);
  // Filter the movers tables to match the chart selection (single group / product)
  const chartFilterProduct = (product: string) => {
    if (chartMode !== 'single') return true;
    if (subOfProduct(product)?.id !== effGroup) return false;
    return effProd === 'ALL' || product === effProd;
  };
  // Chart data + series for the selected mode
  const chartConfig = useMemo(() => {
    if (!chartSrc.length) return null;
    const showClosure = !isDaily && (chartMode === 'total' || chartMode === 'stackGroup');
    if (chartMode === 'total') {
      if (isDaily) return { data: chartSrc, series: [
        { key: 'receipts', name: 'Receipts', color: '#2e7d32' },
        { key: 'sales', name: 'Sales', color: '#4a90d9' },
        { key: 'consumption', name: 'Consumption', color: '#e67e22' },
      ], showClosure: false, stacked: false };
      // netProduction, not the signed total. Summing every grade nets a feedstock's negative
      // production against the products made from it, so the bars sat around zero and swung
      // negative -- the same artefact the headline card had, in chart form, directly beneath the
      // card that now reads "excludes feedstock". Both now say the same thing.
      return { data: chartSrc, series: [{ key: 'netProduction', name: 'Net production', color: '#4a90d9' }], showClosure, stacked: false };
    }
    if (chartMode === 'single') {
      const data = chartSrc.map((b: any) => {
        let v = 0;
        Object.entries(b.byProduct).forEach(([prod, pv]: any) => {
          if (subOfProduct(prod)?.id !== effGroup) return;
          if (effProd === 'ALL' || prod === effProd) v += Math.abs(chartValueOf(pv));
        });
        return { label: b.label, value: v };
      });
      const name = effProd === 'ALL' ? `${groupLabelOf(effGroup)} (all)` : effProd;
      const color = CAT_COLOR[(mapping?.subCategories || SUBCATS).find((s) => s.id === effGroup)?.category || ''] || '#4a7fbf';
      return { data, series: [{ key: 'value', name, color }], showClosure: false, stacked: false };
    }
    // stacked by group (sub-category) — distinct color per group so segments read solid
    const keys = breakdownOptions.groups.map((g) => g.id);
    const data = chartSrc.map((b: any) => {
      const row: any = { label: b.label };
      keys.forEach((k) => { row[k] = 0; });
      Object.entries(b.byProduct).forEach(([prod, pv]: any) => {
        const k = subOfProduct(prod)?.id || 'unassigned';
        if (row[k] !== undefined) row[k] += Math.abs(chartValueOf(pv));
      });
      return row;
    });
    const series = keys.map((k, i) => ({ key: k, name: groupLabelOf(k), color: PALETTE[i % PALETTE.length], stackId: 'a' }));
    return { data, series, showClosure, stacked: true };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartSrc, isDaily, mapping, chartMode, effGroup, effProd, breakdownOptions, chartValueOf]);

  // Phase 3 — loss-% thresholds & "grossly wrong" alerts.
  // NOTE the vocabulary: the IDENTIFIERS below still say "closure" (closurePct, avgClosure,
  // showClosure) while everything the user sees says "loss %". Deliberate — renaming the internals is
  // a wide diff for no user benefit. The figure is unaccounted loss / crude processed in both cases.
  // The warn threshold IS the dataset's Mass Balance Target (Settings → Mass Balance). It used to be
  // a separate per-browser "Warn %" box defaulting to 0.5, which could disagree with the target the
  // Mass Gain/Loss chart judges against -- two numbers both called the limit. Critical stays local.
  // "Outside" means strictly beyond the target, matching the chart (a month AT -0.50% is within).
  const warnPct = (typeof mbSettings?.marginPct === 'number' && isFinite(mbSettings.marginPct) && mbSettings.marginPct > 0)
    ? mbSettings.marginPct : DEFAULT_MB_MARGIN_PCT;
  const muiTheme = useTheme();
  const targetColor = muiTheme.palette.mode === 'dark' ? muiTheme.palette.text.primary : '#000';
  const severityOf = (pct: number) => { const a = Math.abs(pct); return a >= critPct ? 'critical' : a > warnPct ? 'warn' : 'ok'; };
  const setCrit = (v: number) => { setCritPct(v); localStorage.setItem('mb-crit-pct', String(v)); };
  const alerts = useMemo(() => buckets
    .map((b) => ({ label: b.label, closurePct: b.closurePct, sev: severityOf(b.closurePct) }))
    .filter((a) => a.sev !== 'ok')
    .sort((x, y) => Math.abs(y.closurePct) - Math.abs(x.closurePct)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buckets, warnPct, critPct]);
  const critCount = alerts.filter((a) => a.sev === 'critical').length;

  const movMacro = useMemo(() => {
    if (!movDaily.length) return null;
    const tot = movDaily.reduce((a, b) => ({ receipts: a.receipts + b.receipts, sales: a.sales + b.sales, consumption: a.consumption + b.consumption }), { receipts: 0, sales: 0, consumption: 0 });
    return { n: movDaily.length, first: movDaily[0].label, last: movDaily[movDaily.length - 1].label, tot };
  }, [movDaily]);
  const movMovers = useMemo(() => {
    if (!movDaily.length) return [] as any[];
    const cur = movDaily[movDaily.length - 1], prev = movDaily.length > 1 ? movDaily[movDaily.length - 2] : null;
    const names = new Set<string>([...Object.keys(cur.byProduct), ...(prev ? Object.keys(prev.byProduct) : [])]);
    return Array.from(names).map((n) => {
      const c = cur.byProduct[n] || { net: 0 }; const p = prev ? prev.byProduct[n] : null;
      const cv = c.net, pv = p ? p.net : 0;
      return { product: n, cur: cv, prev: pv, delta: cv - pv };
    }).filter((r) => Math.abs(r.cur) > 0.5 || Math.abs(r.prev) > 0.5).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  }, [movDaily]);

  // balM is null for volume-only (OAS) datasets — fall back to balV so nothing downstream sees null.
  const rows: BalanceRow[] = useMemo(() => (result ? (basis === 'volume' ? result.balV : (result.balM || result.balV)) : []), [result, basis]);
  const totals = useMemo(() => rows.reduce((a, r) => ({
    opening: a.opening + r.opening, receipts: a.receipts + r.receipts, production: a.production + r.production,
    shipments: a.shipments + r.shipments, reclass: a.reclass + r.reclass, closing: a.closing + r.closing,
    theoretical: a.theoretical + r.theoretical, gainLoss: a.gainLoss + r.gainLoss,
  }), { opening: 0, receipts: 0, production: 0, shipments: 0, reclass: 0, closing: 0, theoretical: 0, gainLoss: 0 }), [rows]);

  // Yield & own-use: what the refinery made, its own-use/loss streams, and volume gain.
  const computeYield = useCallback((rowsArg: BalanceRow[]) => computeYieldPure(rowsArg, mapping, oasAssignments), [mapping, oasAssignments]);
  const yieldSummary = useMemo(() => (result ? computeYield(rows) : null), [result, rows, computeYield]);
  // Both bases computed so the KPI strip can show m³ AND MT side by side (SCC1). balM absent (e.g.
  // OAS, volume-only) => the MT figure is simply omitted per the data-driven-display rule.
  const yieldV = useMemo(() => (result?.balV ? computeYield(result.balV) : null), [result, computeYield]);
  const yieldM = useMemo(() => (result?.balM && result.balM.length ? computeYield(result.balM) : null), [result, computeYield]);
  const prevRows = useMemo<BalanceRow[] | null>(() => (result?.prevBalV ? (basis === 'volume' ? result.prevBalV : result.prevBalM) : null), [result, basis]);
  const yieldPrev = useMemo(() => (prevRows ? computeYield(prevRows) : null), [prevRows, computeYield]);
  const scrollToSection = (subId: string) => { const el = document.getElementById(`mbsec-${subId}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); };

  const sub = (rs: BalanceRow[]) => rs.reduce((a, r) => ({
    opening: a.opening + r.opening, receipts: a.receipts + r.receipts, production: a.production + r.production,
    shipments: a.shipments + r.shipments, reclass: a.reclass + r.reclass, closing: a.closing + r.closing,
    theoretical: a.theoretical + r.theoretical, gainLoss: a.gainLoss + r.gainLoss,
  }), { opening: 0, receipts: 0, production: 0, shipments: 0, reclass: 0, closing: 0, theoretical: 0, gainLoss: 0 });

  const groups = useMemo(() => {
    const cats = mapping?.categories || CATEGORIES;
    const subs = mapping?.subCategories || SUBCATS;
    const sid = (product: string) => mapping?.assignments[product] || oasAssignments?.[product] || heuristicSubCat(product) || '';
    const src = rows.filter((r) => !hideInactive || r.active);
    const byName = (a: BalanceRow, b: BalanceRow) => a.product.localeCompare(b.product);
    const bySub = new Map<string, BalanceRow[]>();
    src.forEach((r) => { const s = sid(r.product); if (!bySub.has(s)) bySub.set(s, []); bySub.get(s)!.push(r); });
    const out: { key: string; cat: string; label: string; rows: BalanceRow[] }[] = [];
    cats.forEach((c) => subs.filter((s) => s.category === c.id).forEach((s) => {
      if (bySub.has(s.id)) out.push({ key: s.id, cat: c.id, label: `${c.label} · ${s.label}`, rows: bySub.get(s.id)!.sort(byName) });
    }));
    const known = new Set(subs.map((s) => s.id));
    Array.from(bySub.keys()).filter((k) => !known.has(k)).forEach((k) => out.push({ key: k || 'unassigned', cat: '', label: k ? `? · ${k}` : 'Unassigned', rows: bySub.get(k)!.sort(byName) }));
    return out;
  }, [rows, hideInactive, mapping, oasAssignments]);
  const hiddenCount = rows.filter((r) => !r.active).length;

  // products seen across loaded reports, for the mapping editor
  const allProducts = useMemo(() => {
    const m = new Map<string, 'crude' | 'product'>();
    periods.filter((p) => p.ready).forEach((p) => {
      try { computeBalance(parseProductionTab(p.prod.wb.Sheets['Volume'])).forEach((r) => { if (!m.has(r.product)) m.set(r.product, r.category); }); } catch { /* skip */ }
    });
    if (result?.balV) result.balV.forEach((r: BalanceRow) => { if (!m.has(r.product)) m.set(r.product, r.category); });
    return Array.from(m.entries()).map(([name, category]) => ({ name, category }));
  }, [periods, result]);

  const saveMappingNow = async () => { if (mapping) { await saveMapping(mapping, dataset, await idToken()); setMappingSaved(true); } };

  // resolve a product to its mapped category · sub-category (for the trends movers chip)
  const productGroup = (product: string) => {
    const sid = mapping?.assignments[product] || oasAssignments?.[product] || heuristicSubCat(product) || '';
    const sd = (mapping?.subCategories || SUBCATS).find((s) => s.id === sid);
    return sd ? { label: `${catLabel(sd.category)} · ${sd.label}`, cat: sd.category } : { label: 'Unassigned', cat: '' };
  };

  // Open the editor. First time (nothing saved yet), seed assignments from the current
  // heuristic so it loads exactly as the products are grouped right now.
  const openMapping = () => {
    if (!mappingSaved && mapping) {
      const assignments = { ...mapping.assignments };
      allProducts.forEach((p) => { if (!assignments[p.name]) { const s = oasAssignments?.[p.name] || heuristicSubCat(p.name); if (s) assignments[p.name] = s; } });
      setMapping({ ...mapping, assignments });
    }
    setMappingOpen(true);
  };

  const numCells = (t: any, plain = false) => (<>
    <TableCell align="right">{fmt(t.opening)}</TableCell>
    <TableCell align="right">{fmt(t.receipts)}</TableCell>
    <TableCell align="right">{fmt(t.production)}</TableCell>
    <TableCell align="right">{fmt(t.shipments)}</TableCell>
    <TableCell align="right">{fmt(t.reclass)}</TableCell>
    <TableCell align="right">{fmt(t.theoretical)}</TableCell>
    <TableCell align="right">{fmt(t.closing)}</TableCell>
    <TableCell align="right" sx={plain ? undefined : { color: Math.abs(t.gainLoss) < 1 ? 'text.disabled' : 'warning.main', fontWeight: Math.abs(t.gainLoss) >= 1 ? 700 : 400 }}>{fmt(t.gainLoss)}</TableCell>
  </>);

  const totalInputs = totals.opening + totals.receipts + Math.max(0, totals.production);
  const unaccountedPct = totalInputs ? (totals.gainLoss / totalInputs) * 100 : 0;
  const unit = basis === 'volume' ? volUnit : massUnit;
  // The site's OWN Fuel & Loss (from the customer workbook). When present, THIS is the real physical loss
  // shown in the summary; the stock reconciliation residual is only a data-integrity/ledger-closure check.
  const fl = result?.fuelLoss as FuelLossReport | undefined;
  const hasFuelLoss = !!fl;
  const flLoss = fl?.unaccountedLoss ?? null;                 // real unaccounted loss (or Loss/Gain for AORA)
  const flLossPct = fl?.lossPct ?? null;                      // fraction (0.0094 = 0.94%)
  const flUnit = fl ? (fl.units === 'MT' ? 'MT' : (fl.basis?.mass?.unit || 'MT')) : '';
  // The site's MTD sheet literally labels this cell "Unaccounted LOSS". Calling it "Accounted loss"
  // was our own invention and it inverted the meaning: under HM 31, ACCOUNTED loss is Flare + BS&W,
  // and what is left after fuel and those is the UNACCOUNTED loss. Cal repeated the wrong word back
  // to the customer and concluded there was "no unaccounted loss" when the site reports 2,666 MT.
  const flLossLabel = fl?.source === 'aora' ? 'Loss / gain' : 'Unaccounted Loss';

  const exportXlsx = () => {
    if (!result) return;
    const header = ['Product', 'Opening', 'Receipts', 'Production', 'Shipments', 'Reclass', 'Theoretical Close', 'Actual Close', 'Gain/Loss'];
    const rowArr = (label: string, t: any) => [label, t.opening, t.receipts, t.production, t.shipments, t.reclass, t.theoretical, t.closing, t.gainLoss];
    const cats = mapping?.categories || CATEGORIES;
    const subs = mapping?.subCategories || SUBCATS;
    const sid = (r: BalanceRow) => mapping?.assignments[r.product] || oasAssignments?.[r.product] || heuristicSubCat(r.product) || '';
    const buildSheet = (bal: BalanceRow[]) => {
      const src = bal.filter((r) => !hideInactive || r.active);
      const aoa: any[] = [[`Refinery Mass Balance — ${result.isRange ? result.label : keyLabel(result.key)}`], header];
      const bySub = new Map<string, BalanceRow[]>();
      src.forEach((r) => { const s = sid(r); if (!bySub.has(s)) bySub.set(s, []); bySub.get(s)!.push(r); });
      const emit = (id: string, label: string) => {
        const rs = (bySub.get(id) || []).sort((a, b) => a.product.localeCompare(b.product));
        if (!rs.length) return;
        aoa.push([label.toUpperCase()]);
        rs.forEach((r) => aoa.push(rowArr(r.product, r)));
        aoa.push(rowArr(`${label} subtotal`, sub(rs)));
      };
      cats.forEach((c) => subs.filter((s) => s.category === c.id).forEach((s) => emit(s.id, `${c.label} · ${s.label}`)));
      const known = new Set(subs.map((s) => s.id));
      Array.from(bySub.keys()).filter((k) => !known.has(k)).forEach((k) => emit(k, k ? `? · ${k}` : 'Unassigned'));
      aoa.push(rowArr('REFINERY TOTAL', sub(src)));
      return XLSX.utils.aoa_to_sheet(aoa);
    };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildSheet(result.balV), `Volume (${volUnit})`);
    XLSX.utils.book_append_sheet(wb, buildSheet(result.balM), `Mass (${massUnit})`);
    XLSX.writeFile(wb, `Mass Balance ${result.key}.xlsx`);
  };

  const reset = () => {
    setProdEntries([]); setMovEntries([]); setInvEntries([]); setResult(null); setError(null);
    if (prodInput.current) prodInput.current.value = ''; if (movInput.current) movInput.current.value = ''; if (invInput.current) invInput.current.value = '';
  };

  const UploadSlot = ({ label, sub, count, names, onFiles, inputRef }: any) => (
    <Paper variant="outlined"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e: React.DragEvent) => e.preventDefault()}
      onDrop={(e: React.DragEvent) => { e.preventDefault(); const fs = Array.from(e.dataTransfer.files || []); if (fs.length) onFiles(fs); }}
      sx={{ p: 2.5, cursor: 'pointer', borderStyle: 'dashed', borderWidth: 2, flex: 1, minWidth: 260,
        borderColor: count ? 'success.main' : 'divider', bgcolor: count ? 'action.hover' : 'transparent' }}>
      <input ref={inputRef} type="file" accept=".xlsx,.xlsm,.xls" hidden multiple onChange={(e) => { const fs = Array.from(e.target.files || []); if (fs.length) onFiles(fs); }} />
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {count ? <CheckCircle color="success" /> : <RadioButtonUnchecked color="disabled" />}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2">{label}{count ? ` · ${count} loaded` : ''}</Typography>
          {count ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{names.join(' · ')}</Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{sub} — drop one or many</Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );

  // Shared production/movement chart with mode selector — used by both month+ and day/week views
  const chartPanel = (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">{isDaily ? 'Movement' : (chartMode === 'total' ? 'Net production' : 'Production')} over time ({unit})</Typography>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={chartMode} onChange={(_e, v) => v && setChartMode(v)}>
          <ToggleButton value="total">Total</ToggleButton>
          <ToggleButton value="stackGroup">By group</ToggleButton>
          <ToggleButton value="single">Single</ToggleButton>
        </ToggleButtonGroup>
        {chartMode === 'single' && (<>
          <TextField select size="small" label="Group" value={effGroup} onChange={(e) => { setChartSel(e.target.value); setChartProd('ALL'); }} sx={{ minWidth: 190 }}>
            {breakdownOptions.groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Product" value={effProd} onChange={(e) => setChartProd(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="ALL"><em>All products in group</em></MenuItem>
            {productsInGroup.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
        </>)}
      </Stack>
      {chartConfig && (
        <ResponsiveContainer width="100%" height={chartConfig.series.length > 6 ? 300 + Math.min(160, Math.ceil(chartConfig.series.length / 3) * 22) : 300}>
          <ComposedChart data={chartConfig.data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="l" tick={{ fontSize: 11 }} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)} />
            {chartConfig.showClosure && <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${Number(v).toFixed(1)}%`} />}
            <RTooltip wrapperStyle={{ zIndex: 1600 }} contentStyle={{ fontSize: 12 }} itemStyle={{ padding: 0 }} formatter={(v: any, n: any) => (n === 'Loss %' ? `${Number(v).toFixed(3)}%` : fmt(Number(v)))} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
            {/* Target = black dashed, as on the Mass Gain/Loss chart; critical stays red. */}
            {chartConfig.showClosure && [warnPct, -warnPct, critPct, -critPct].map((y, i) => (
              <ReferenceLine key={i} yAxisId="r" y={y} stroke={Math.abs(y) === critPct ? '#d32f2f' : targetColor}
                strokeWidth={Math.abs(y) === critPct ? 1 : 1.5} strokeDasharray={Math.abs(y) === critPct ? '4 4' : '6 3'} />
            ))}
            {chartConfig.series.map((s: any) => (
              <Bar key={s.key} yAxisId="l" dataKey={s.key} name={s.name} fill={s.color} stackId={s.stackId} radius={chartConfig.stacked ? undefined : [2, 2, 0, 0]} />
            ))}
            {chartConfig.showClosure && (
              <Line yAxisId="r" dataKey="closurePct" name="Loss %" stroke="#e67e22" strokeWidth={2}
                dot={(p: any) => { const sev = severityOf(p.payload.closurePct); const fill = sev === 'critical' ? '#d32f2f' : sev === 'warn' ? '#ed6c02' : '#2e7d32'; return <circle key={`${p.cx}-${p.cy}`} cx={p.cx} cy={p.cy} r={sev === 'ok' ? 2.5 : 4.5} fill={fill} stroke="#fff" strokeWidth={1} />; }} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {chartMode === 'total' ? (isDaily ? 'Receipts / sales / consumption over time.' : 'Net production (excludes feedstock) with loss % (dashed = warn/critical).')
          : chartMode === 'single' ? (effProd === 'ALL' ? 'The whole selected group over time. Pick a product to narrow it.' : 'One product over time.')
          : `Total ${isDaily ? 'movement' : 'production'} split into groups (stacked) in your Product-Mapping order — first group at the bottom, last on top.`}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      {dsLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, p: 1.25, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Loading <strong>{dataset}</strong>…</Typography>
        </Box>
      )}
      {!dsLoading && (loading || progress) && (
        <Box sx={{ mb: 2, p: 1.25, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: progress && progress.total > 1 ? 1 : 0 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {progress ? progress.label : 'Working…'}{progress && progress.total > 1 ? ` (${Math.min(progress.done + 1, progress.total)} of ${progress.total})` : ''}
            </Typography>
          </Box>
          {progress && progress.total > 1 && <LinearProgress variant="determinate" value={Math.round((progress.done / progress.total) * 100)} />}
        </Box>
      )}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Mass Balance Utility</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {oasPeriods.length && !periods.length ? (
          <>Upload the site's monthly reports — <strong>Daily Yields</strong> (m³), <strong>MT report</strong> (MT), <strong>Material Download</strong> and <strong>Ext Movement</strong> — one month or many;
            they are saved to the dataset and each month appears as a chip below. Pick a month for the per-product balance in <strong>{volUnit} and {massUnit}</strong> (measured tonnes when the MT report is present),
            use <strong>Date range</strong> to chain several months into one combined balance, or <strong>Trends</strong> for month-over-month analytics.</>
        ) : (
          <>Upload your <strong>Production reports</strong> (Volume/Mass) and <strong>Movement reports</strong>
            (Receipts &amp; Sales) — one month or many. We match each production month to a movement file that covers it and
            list every period we can generate. Pick a month to build the per-product refinery mass balance in
            <strong> {volUnit} and {massUnit}</strong>, with movement reconciliation and gain/loss. Nothing is saved; results clear on leave.</>
        )}
      </Typography>

      {authExpired && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Refresh</Button>}>
          Your session expired, so saved uploads couldn&apos;t load. <strong>Your data is safe</strong> — refresh to sign back in.
        </Alert>
      )}

      {/* Loaded-data status (upload is the single "Upload data" control at the top of the hub now) */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        {dsLoading ? (
          <><CircularProgress size={16} /><Typography variant="body2" color="text.secondary">Loading <strong>{dataset}</strong>…</Typography></>
        ) : result?.oas ? (
          <Typography variant="body2" color="text.secondary">Balance <strong>adapted from this dataset's Daily Yields</strong> — {result.balV?.length || 0} grades.</Typography>
        ) : (
          <>
            {oasPeriods.length && !periods.length ? (
              <Typography variant="body2" color="text.secondary">
                Saved months: <strong>{oasPeriods.filter((m) => /^\d{4}-\d{2}$/.test(m)).length}</strong>
                {coverage ? <> · {coverage.minM} → {coverage.maxM}{coverage.gaps.length ? ` · gaps: ${coverage.gaps.join(', ')}` : ''}</> : null}
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Loaded: <strong>{prodEntries.length}</strong> production · <strong>{movEntries.length}</strong> movement · <strong>{invEntries.length}</strong> inventory
                </Typography>
                {prodEntries.length === 0 && <Typography variant="caption" color="text.secondary">— use “Upload data” at the top of the page.</Typography>}
              </>
            )}
          </>
        )}
      </Stack>

      {/* First thing on the page, per Leslie: "It shows overall health of the process. Can make it
          look similar to Meters? Overall health and then drill down." */}
      {result && <MassGainLossOverview series={lossSeries} massUnit={massUnit} marginPct={mbSettings?.marginPct ?? null} />}

      {(periods.length > 0 || result) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: periods.length > 0 ? 1 : 0 }} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2">{periods.length > 0 ? `Periods — ${readyCount} of ${periods.length} ready to generate` : (result?.oas ? 'Yields-sourced dataset' : 'Dataset')}</Typography>
            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" size="small" onClick={openMapping}>Product mapping</Button>
            <ToggleButtonGroup size="small" exclusive value={viewMode}
              onChange={(_e, v) => { if (!v) return; setViewMode(v); if (v === 'trends') genTrends(); if (v === 'range') setResult(null); }}>
              <ToggleButton value="period">Single month</ToggleButton>
              <ToggleButton value="range">Date range</ToggleButton>
              <ToggleButton value="trends">Trends &amp; analytics</ToggleButton>
            </ToggleButtonGroup>
            <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={reset}>Reset</Button>
          </Stack>
          {periods.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {periods.map((p) => (
              <Chip key={p.key} label={p.label}
                icon={loading && result?.key !== p.key ? undefined : (p.ready ? <CheckCircle /> : <RadioButtonUnchecked />)}
                color={p.ready ? (result?.key === p.key ? 'primary' : 'success') : 'default'}
                variant={p.ready ? 'filled' : 'outlined'}
                clickable={p.ready} disabled={!p.ready}
                onClick={p.ready ? () => genPeriod(p) : undefined} />
            ))}
          </Stack>
          )}
          {readyCount < periods.length && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Greyed months have a production report but no movement file covering them yet — add the movement file to enable.
            </Typography>
          )}
          {/* OAS-sourced months — one chip per stored OAS month; click to view that month. */}
          {oasPeriods.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {oasPeriods.map((p) => (
                <Chip key={p} label={p} icon={<CheckCircle />}
                  color={result?.key === p ? 'primary' : 'success'} variant="filled"
                  clickable onClick={() => loadOasMonth(p)} />
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {result?.oasDerivedMass && basis === 'mass' && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>Estimated data — not measured.</strong> Every {massUnit} figure shown is <strong>derived from density</strong>
          {' '}(measured density where available, otherwise a reference-band estimate), because only the m³ report is loaded for this month.
          Upload the month's <strong>MT report</strong> (MT) workbook for measured tonnes, or switch to <strong>{volUnit}</strong> for the source values.
        </Alert>
      )}
      {result?.oasMassMeasured && basis === 'mass' && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <strong>Measured tonnes.</strong> {massUnit} figures are read directly from the site's MT report (MT report) — no density estimate. Volume ({volUnit}) comes from the Daily Yields report.
        </Alert>
      )}
      {result?.oasVolumeBasis === 'MT' && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>Only the MT report is loaded for this month.</strong> The figures shown under <strong>{volUnit}</strong> are actually tonnes from the MT report. Upload the month's Daily Yields (m³) workbook for true volumes.
        </Alert>
      )}

      {viewMode === 'range' && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">Combined balance for range:</Typography>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={effFrom}
              inputProps={{ min: coverage?.from, max: coverage?.to }} onChange={(e) => setFromDate(e.target.value)} sx={{ width: 158 }} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={effTo}
              inputProps={{ min: coverage?.from, max: coverage?.to }} onChange={(e) => setToDate(e.target.value)} sx={{ width: 158 }} />
            <Button variant="contained" size="small" onClick={genRange} disabled={!coverage || loading} startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}>{loading ? 'Generating…' : 'Generate combined balance'}</Button>
            {coverage && <Typography variant="caption" color={coverage.gaps.length ? 'warning.main' : 'text.secondary'}>Uploaded {coverage.minM} → {coverage.maxM}{coverage.gaps.length ? ` · gaps: ${coverage.gaps.join(', ')}` : ' · continuous'}</Typography>}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Chains opening (first month) → closing (last month), summing receipts/production/shipments/reclass across every month in the range. Most meaningful on a continuous range.
          </Typography>
        </Paper>
      )}

      {(viewMode === 'period' || viewMode === 'range') && result && (
        <Box sx={{ mt: 2 }}>
          {result.isRange && result.rangeRestatement && Math.abs(basis === 'mass' ? (result.rangeRestatement.mass ?? 0) : result.rangeRestatement.volume) > 0.5 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Month-boundary restatements: {(basis === 'mass' ? (result.rangeRestatement.mass ?? 0) : result.rangeRestatement.volume) >= 0 ? '+' : ''}{fmt(basis === 'mass' ? (result.rangeRestatement.mass ?? 0) : result.rangeRestatement.volume)} {unit}.</strong>{' '}
              The site's own reports restate opening stock between months (re-gauging, reclassification). That amount is part of the combined-range residual and is <em>not</em> a physical loss — use the Fuel &amp; Loss figures for the real loss over the range.
            </Alert>
          )}
          {result.isRange && result.rangeGaps?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This range is <strong>not continuous</strong> — missing {result.rangeGaps.join(', ')}. Opening→closing won't reconcile cleanly across the gap; pick a continuous range for an accurate balance.
            </Alert>
          )}
          {/* Summary bar. The full Fuel & Loss breakdown is rendered after the Product slate below. */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip icon={<InsertDriveFile />} label={result.isRange ? result.label : keyLabel(result.key)} />
              <Typography variant="body2"><strong>{rows.filter((r) => r.active).length}</strong> active products</Typography>
              <Divider orientation="vertical" flexItem />
              {hasFuelLoss ? (
                <>
                  <Tooltip title={`The site's own ${flLossLabel.toLowerCase()} for this period: ${flLoss != null ? fmt(flLoss) : '—'} ${flUnit}${flLossPct != null ? ` (${(flLossPct * 100).toFixed(2)}% of crude)` : ''} — fuel burned + flare + BS&W + unaccounted, read from the site's own balance sheet. This is the real physical loss. Click "Explain this loss" for the full breakdown. Separately, the stock movement ledger reconciles to ${totals.gainLoss >= 0 ? '+' : ''}${fmt(totals.gainLoss)} ${unit} ≈ 0 (a data-integrity check).`}>
                    <Typography variant="body2" sx={{ cursor: 'help' }}>{flLossLabel}: <strong>{flLossPct != null ? `${(flLossPct * 100).toFixed(2)}%` : '—'}</strong> <Typography component="span" variant="caption" color="text.secondary">({flLoss != null ? `${flLoss >= 0 && fl?.source === 'aora' ? '+' : ''}${fmt(flLoss)}` : '—'} {flUnit}{fl?.source === 'oas' ? ' · site report, always MT' : ''})</Typography></Typography>
                  </Tooltip>

                  <Tooltip title={`Stock movement ledger residual = ${totals.gainLoss >= 0 ? '+' : ''}${fmt(totals.gainLoss)} ${unit} (${unaccountedPct.toFixed(3)}% of input) — confirms the movement ledger reconciles; not the physical loss.`}>
                    <Chip size="small" variant="outlined" color={Math.abs(unaccountedPct) <= 0.5 ? 'success' : 'warning'} label={Math.abs(unaccountedPct) <= 0.5 ? 'ledger reconciles' : 'ledger off'} />
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title={`The mass-balance residual (theoretical − actual closing) = ${totals.gainLoss >= 0 ? '+' : ''}${fmt(totals.gainLoss)} ${unit}. Shown here as ${unaccountedPct.toFixed(3)}% of total INPUT (opening + receipts) — the standard refinery unaccounted-loss KPI. The Product-slate "Loss / balance" bar is the SAME number shown as a % of total PRODUCTION instead, which is why the two percentages differ.`}>
                    <Typography variant="body2" sx={{ cursor: 'help' }}>Ledger residual: <strong>{unaccountedPct.toFixed(3)}%</strong> <Typography component="span" variant="caption" color="text.secondary">of input ({totals.gainLoss >= 0 ? '+' : ''}{fmt(totals.gainLoss)} {unit})</Typography></Typography>
                  </Tooltip>
                  <Chip size="small" color={Math.abs(unaccountedPct) <= 0.5 ? 'success' : 'error'}
                    label={Math.abs(unaccountedPct) <= 0.5 ? 'within ±0.5%' : 'investigate'} />
                </>
              )}
              <Box sx={{ flex: 1 }} />
              {hiddenCount > 0 && (
                <Chip size="small" clickable variant={hideInactive ? 'filled' : 'outlined'}
                  label={hideInactive ? `${hiddenCount} inactive hidden` : 'showing all'}
                  onClick={() => setHideInactive((v) => !v)} />
              )}
              <ToggleButtonGroup size="small" exclusive value={basis} onChange={(_e, v) => v && setBasis(v)}>
                <ToggleButton value="volume">{volToggle}</ToggleButton>
                <Tooltip title={result.oasDerivedMass ? 'Mass (MT) is estimated from density — shown with ≈ (est.). Upload the MT report workbook for measured tonnes.' : (result.balM ? '' : 'This dataset has no mass source (volume only).')}>
                  <span><ToggleButton value="mass" disabled={!result.balM}>{massToggle}</ToggleButton></span>
                </Tooltip>
              </ToggleButtonGroup>
              <Button size="small" variant="outlined" startIcon={<Download />} onClick={exportXlsx}>Export</Button>
            </Stack>
          </Paper>

          {result.oas && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This dataset's data comes from the <strong>Daily Yields</strong> feed (adapted for the Refinery view), in <strong>m³</strong>.
              {' '}{result.oasDerivedMass
                ? <>Mass (MT) is <strong>estimated from density</strong> — measured per grade where available, else the grade's group-average measured density (density known for <strong>{Math.round((result.oasMassCoverage || 0) * 100)}%</strong> of throughput). Marked <strong>≈ (est.)</strong> — not a measured mass.</>
                : <>Mass (MT) is <strong>not shown</strong> — density couldn't be resolved for enough of this dataset to be trustworthy.</>}
              {' '}The movement / tank-gauging reconciliations need feeds we don't have for this dataset, so they're hidden.
            </Alert>
          )}

          {/* Yield & own-use */}
          {yieldSummary && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2">{`Processed & produced — ${volUnit}${yieldM ? ` & ${massUnit}` : ''}`}</Typography>
                <Box sx={{ flex: 1 }} />
                {result.key && !result.isRange && (
                  <Chip size="small" clickable color="secondary" variant="outlined" icon={<QuestionAnswer sx={{ fontSize: 16 }} />}
                    label="Monthly Mass Balance Checklist" onClick={() => setChecklistOpen(true)} sx={{ mr: 1 }} />
                )}
                {result.prevKey && (
                  <Chip size="small" clickable variant={momCompare ? 'filled' : 'outlined'} color={momCompare ? 'primary' : 'default'}
                    label={momCompare ? `vs ${keyLabel(result.prevKey)}` : 'Compare to last month'}
                    onClick={() => setMomCompare((v) => !v)} />
                )}
              </Stack>

              {/* SCC1 — five totals, each in m³ AND MT; a card renders only when its stream exists in the
                  loaded data (e.g. no flaring stream => no Flaring card). MT omitted when there's no mass
                  source (volume-only datasets); derived MT with a flag arrives with the OAS adapter (P3d). */}
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {[
                  { k: 'Total Crude Processed', show: yieldV?.kpi.hasCrude, v: yieldV?.kpi.crudeProc ?? 0, m: yieldM?.kpi.crudeProc ?? 0 },
                  { k: 'Total Other Feedstock Processed', show: yieldV?.kpi.hasOtherFeed, v: yieldV?.kpi.otherFeedProc ?? 0, m: yieldM?.kpi.otherFeedProc ?? 0 },
                  { k: 'Total Net Products Produced', show: yieldV?.kpi.hasProducts, v: yieldV?.kpi.netProducts ?? 0, m: yieldM?.kpi.netProducts ?? 0 },
                  { k: 'Total Other Net Products Produced', show: yieldV?.kpi.hasOtherProducts, v: yieldV?.kpi.otherNetProducts ?? 0, m: yieldM?.kpi.otherNetProducts ?? 0 },
                  { k: 'Total Known Flaring Losses', show: yieldV?.kpi.hasFlaring, v: yieldV?.kpi.flaring ?? 0, m: yieldM?.kpi.flaring ?? 0 },
                  // Informational (NOT a physical loss). OAS: gross→net production adjustment. AORA: stock reclassifications.
                  { k: 'Production Adjustment (→ net)', show: !!result.oas && Math.abs(result.oasAdjustment || 0) > 0.5, v: result.oasAdjustment ?? 0, m: result.oasAdjustmentMass ?? 0, tip: 'The ADJUSTMENTS column — reconciles gross intake & production to NET PRODUCTION (net = intake&prod + adjustments). Informational only: not a physical loss and deliberately kept out of the stock balance.' },
                  { k: 'Reclassifications', show: !result.oas && !!yieldV?.kpi.hasReclass, v: yieldV?.kpi.reclass ?? 0, m: yieldM?.kpi.reclass ?? 0, tip: 'Volume reclassified between grades within the refinery. Part of the balance and nets across grades — not a physical loss.' },
                ].filter((c) => c.show).map((c) => (
                  <Box key={c.k} sx={{ minWidth: 175 }}>
                    {(c as any).tip
                      ? <Tooltip title={(c as any).tip}><Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>{c.k}</Typography></Tooltip>
                      : <Typography variant="caption" color="text.secondary">{c.k}</Typography>}
                    <Typography variant="h6">{fmt(c.v)} <Typography component="span" variant="caption" color="text.secondary">{volUnit}</Typography></Typography>
                    {yieldM && (
                      <Tooltip title={result.oasDerivedMass ? 'Estimated from density (measured density where available, else a reference band) — not a measured mass.' : ''}>
                        <Typography variant="body2" color={result.oasDerivedMass ? 'warning.main' : 'text.secondary'}>
                          {result.oasDerivedMass ? '≈ ' : ''}{fmt(c.m)} {massUnit}{result.oasDerivedMass ? ' (est.)' : ''}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Stack>

              {/* Fuel & Loss — always-visible line items (MT, from the site's own report). The full trend +
                  feed/product breakdown is in the Fuel & Loss section below. */}
              {hasFuelLoss && fl?.source === 'oas' && (
                <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'error.light' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Fuel &amp; Loss <Typography component="span" variant="caption" color="text.secondary">— from the site's own report (mass, MT)</Typography></Typography>
                  <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                    {[
                      { k: 'Own Fuel + NG', v: fl?.totalFuel, sub: fl?.fuelPct != null ? `${(fl.fuelPct * 100).toFixed(2)}% of crude` : '', color: 'warning.main' },
                      { k: 'Flare', v: fl?.flare, sub: '', color: 'error.main' },
                      { k: 'BS&W', v: fl?.bsw, sub: '', color: 'info.main' },
                      { k: 'Unaccounted Loss', v: fl?.unaccountedLoss, sub: fl?.lossPct != null ? `${(fl.lossPct * 100).toFixed(2)}% of crude` : '', color: 'error.main', strong: true },
                    ].map((c) => (
                      <Box key={c.k} sx={{ minWidth: 150 }}>
                        <Typography variant="caption" color="text.secondary">{c.k}</Typography>
                        <Typography variant="h6" sx={c.strong ? { color: c.color, fontWeight: 700 } : undefined}>{c.v != null ? fmt(c.v) : '—'} <Typography component="span" variant="caption" color="text.secondary">MT</Typography></Typography>
                        {c.sub && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{c.sub}</Typography>}
                      </Box>
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Products + fuel + flare + BS&amp;W + unaccounted loss = 100% of crude. The <strong>Fuel &amp; Loss</strong> section below has the disposition chart, monthly trend and yields.
                  </Typography>
                </Box>
              )}

              {/* Balance check — Inputs vs Outputs. Even out when there's no loss; the difference IS the
                  unaccounted loss shown below. In = opening+receipts+production, Out = shipments+closing. */}
              {yieldV && (
                <Box sx={{ mb: 2, p: 1.25, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                  <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                    {[
                      { k: 'Total Inputs', sub: 'opening + receipts + production', v: yieldV.balIn, m: yieldM?.balIn, tip: 'Everything available to the balance: opening inventory + receipts + production.' },
                      { k: 'Total Outputs', sub: 'shipments + closing', v: yieldV.balOut, m: yieldM?.balOut, tip: 'Everything accounted for: shipments out + closing inventory.' },
                    ].map((c) => (
                      <Box key={c.k} sx={{ minWidth: 210 }}>
                        <Tooltip title={c.tip}><Typography variant="caption" color="text.secondary" sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>{c.k} <Typography component="span" variant="caption" color="text.disabled">({c.sub})</Typography></Typography></Tooltip>
                        <Typography variant="h6">{fmt(c.v)} <Typography component="span" variant="caption" color="text.secondary">{volUnit}</Typography></Typography>
                        {yieldM && (
                          <Typography variant="body2" color={result.oasDerivedMass ? 'warning.main' : 'text.secondary'}>
                            {result.oasDerivedMass ? '≈ ' : ''}{fmt(c.m ?? 0)} {massUnit}{result.oasDerivedMass ? ' (est.)' : ''}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                  {(() => {
                    const d = yieldV.balIn - yieldV.balOut;
                    const bal = Math.abs(d) < 0.5;
                    return (
                      <Typography variant="caption" sx={{ fontWeight: 600 }} color={bal ? 'success.main' : 'warning.main'}>
                        {hasFuelLoss
                          ? `✓ Stock ledger reconciles. ${flLossLabel} (from the site's report): ${flLoss != null ? fmt(flLoss) : '—'} ${flUnit}${flLossPct != null ? ` (${(flLossPct * 100).toFixed(2)}% of crude)` : ''} — see the Fuel & Loss section below for the fuel / flare / BS&W breakdown.`
                          : (bal
                              ? '✓ Inputs and outputs balance — no unaccounted loss.'
                              : `Difference ${d >= 0 ? '+' : ''}${fmt(d)} ${volUnit} — the unaccounted loss shown below.`)}
                      </Typography>
                    );
                  })()}
                </Box>
              )}

              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Product slate — {slateMode === 'cumulative' ? 'cumulative (crude = 100% reference, products stack)' : 'share of everything made'} · click a group to jump to its table section
                </Typography>
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup size="small" exclusive value={slateMode} onChange={(_e, v) => v && setSlateMode(v)}>
                  <ToggleButton value="share" sx={{ py: 0.1, px: 1, fontSize: '0.7rem' }}>Share</ToggleButton>
                  <ToggleButton value="cumulative" sx={{ py: 0.1, px: 1, fontSize: '0.7rem' }}>Cumulative</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Box sx={{ mt: 1 }}>
                {(() => {
                  // ① Inputs/Outputs section labels, ② loss/gain last bar, ③ share vs cumulative layout.
                  let cum = 0; let lastSection = '';
                  const nodes: React.ReactNode[] = [];
                  yieldSummary.slate.forEach((g: any) => {
                    // NET production per category; outputs are a share of total net output made,
                    // inputs a share of total feed consumed — so every line reconciles with the table below.
                    const base = g.section === 'input' ? yieldSummary.inTotal : yieldSummary.outTotal;
                    const thisPct = base ? (Math.abs(g.net) / base) * 100 : 0;
                    const comparing = momCompare && yieldPrev;
                    const last = comparing ? (yieldPrev!.bySub.get(g.id)?.net || 0) : 0;
                    const lastBase = g.section === 'input' ? yieldPrev?.inTotal : yieldPrev?.outTotal;
                    const lastPct = comparing && lastBase ? (Math.abs(last) / lastBase) * 100 : 0;
                    // cumulative: inputs (crude/feed) = full-width reference; outputs stack end-to-end
                    let leftPct = 0, widthPct = thisPct;
                    if (slateMode === 'cumulative') {
                      if (g.section === 'input') { leftPct = 0; widthPct = 100; }
                      else { leftPct = Math.min(cum, 100); widthPct = thisPct; cum += thisPct; }
                    }
                    if (g.section !== lastSection) {
                      lastSection = g.section;
                      nodes.push(
                        <Typography key={`sec-${g.section}`} variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mt: nodes.length ? 1.2 : 0, mb: 0.3 }}>
                          {g.section === 'input' ? 'Inputs' : 'Outputs'}
                        </Typography>
                      );
                    }
                    nodes.push(
                      <Box key={g.id} sx={{ mb: comparing ? 0.9 : 0.5 }}>
                        <Box onClick={() => scrollToSection(g.id)} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', borderRadius: 1, px: 0.5, '&:hover': { bgcolor: 'action.selected' } }}>
                          <Box sx={{ width: 210, flexShrink: 0, fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.label}</Box>
                          <Box sx={{ flex: 1, bgcolor: 'action.hover', borderRadius: 1, height: 16, overflow: 'hidden', minWidth: 60, position: 'relative' }}>
                            <Box sx={{ position: 'absolute', left: `${leftPct}%`, width: `${Math.max(widthPct, 0.5)}%`, height: '100%', bgcolor: CAT_COLOR[g.cat] || '#4a7fbf' }} />
                          </Box>
                          <Box sx={{ width: 160, textAlign: 'right', fontSize: '0.8rem', flexShrink: 0 }}>{fmt(g.net)} · {thisPct.toFixed(1)}%</Box>
                        </Box>
                        {comparing && (
                          <Box onClick={() => scrollToSection(g.id)} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', px: 0.5, mt: 0.25 }}>
                            <Box sx={{ width: 210, flexShrink: 0, pl: 2, fontSize: '0.72rem', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{keyLabel(result.prevKey)}</Box>
                            <Box sx={{ flex: 1, bgcolor: 'action.hover', borderRadius: 1, height: 10, overflow: 'hidden', minWidth: 60 }}>
                              <Box sx={{ width: `${Math.max(lastPct, 0.5)}%`, height: '100%', bgcolor: `${CAT_COLOR[g.cat] || '#4a7fbf'}66` }} />
                            </Box>
                            <Box sx={{ width: 160, textAlign: 'right', fontSize: '0.72rem', color: 'text.secondary', flexShrink: 0 }}>{fmt(last)} · {lastPct.toFixed(1)}%</Box>
                          </Box>
                        )}
                      </Box>
                    );
                  });
                  // ② loss/gain shown as the last bar (only when there is a residual)
                  const gl = yieldSummary.gainLossTotal;
                  const glPct = totalInputs ? (Math.abs(gl) / totalInputs) * 100 : 0;  // % of total input — SAME base as the header "Ledger residual", so the two agree
                  if (Math.abs(gl) > 0.5) {
                    nodes.push(
                      <Tooltip key="__gainloss" title={`The mass-balance residual (theoretical − actual closing) = ${gl >= 0 ? '+' : ''}${fmt(gl)} ${unit}, shown as ${glPct.toFixed(3)}% of total input. This is the SAME figure as "${hasFuelLoss ? 'Stock closure' : 'Unaccounted loss'}" in the header above${hasFuelLoss ? ' — a ledger-closure residual, NOT the physical loss (see the Fuel & Loss panel).' : ' — inputs minus everything accounted for.'}`}>
                      <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, px: 0.5, cursor: 'help' }}>
                        <Box sx={{ width: 210, flexShrink: 0, fontSize: '0.8rem', fontWeight: 700 }}>Ledger residual <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>(of input)</Typography></Box>
                        <Box sx={{ flex: 1, bgcolor: 'action.hover', borderRadius: 1, height: 16, overflow: 'hidden', minWidth: 60 }}>
                          <Box sx={{ width: `${Math.max(glPct, 0.5)}%`, height: '100%', bgcolor: gl >= 0 ? '#2e7d32' : '#c0392b' }} />
                        </Box>
                        <Box sx={{ width: 160, textAlign: 'right', fontSize: '0.8rem', fontWeight: 700, color: gl >= 0 ? 'success.main' : 'error.main' }}>{gl >= 0 ? '+' : ''}{fmt(gl)} · {glPct.toFixed(3)}%</Box>
                      </Box>
                      </Tooltip>
                    );
                  }
                  // ③ The site's OWN unaccounted loss. The bar above it is the stock-LEDGER residual,
                  // which nets to ~0 by construction -- so without this row the slate never showed the
                  // physical loss at all, even though the caption claims products + fuel + flare + BS&W
                  // + loss = 100% of crude. Measured in the fuel & loss report's unit (MT), which is
                  // independent of the m3/MT toggle above, so the unit is always printed.
                  if (flLoss != null && Math.abs(flLoss) > 0.5) {
                    const lossPct = flLossPct != null ? Math.abs(flLossPct) * 100 : 0;
                    nodes.push(
                      <Tooltip key="__unaccounted" title={`${flLossLabel} of ${fmt(Math.abs(flLoss))} ${flUnit}${flLossPct != null ? ` — ${(Math.abs(flLossPct) * 100).toFixed(2)}% of crude processed` : ''}. This is the PHYSICAL loss from the site's own fuel & loss report, not the ledger residual above it.`}>
                        <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, px: 0.5, cursor: 'help' }}>
                          <Box sx={{ width: 210, flexShrink: 0, fontSize: '0.8rem', fontWeight: 700 }}>
                            {flLossLabel} <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>(of crude)</Typography>
                          </Box>
                          <Box sx={{ flex: 1, bgcolor: 'action.hover', borderRadius: 1, height: 16, overflow: 'hidden', minWidth: 60 }}>
                            <Box sx={{ width: `${Math.min(Math.max(lossPct, 0.5), 100)}%`, height: '100%', bgcolor: '#c0392b' }} />
                          </Box>
                          <Box sx={{ width: 160, textAlign: 'right', fontSize: '0.8rem', fontWeight: 700, color: 'error.main' }}>
                            {fmt(Math.abs(flLoss))} {flUnit} · {lossPct.toFixed(2)}%
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  }
                  return nodes;
                })()}
              </Box>

              {(yieldSummary.dist + yieldSummary.gaso) > 0.5 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Distillate vs Gasoline (tagged production)</Typography>
                  <Box sx={{ display: 'flex', height: 20, borderRadius: 1, overflow: 'hidden', mt: 0.5 }}>
                    <Box sx={{ width: `${100 * yieldSummary.dist / (yieldSummary.dist + yieldSummary.gaso)}%`, bgcolor: '#2e7d32', color: '#fff', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Distillate {(100 * yieldSummary.dist / (yieldSummary.dist + yieldSummary.gaso)).toFixed(0)}%
                    </Box>
                    <Box sx={{ width: `${100 * yieldSummary.gaso / (yieldSummary.dist + yieldSummary.gaso)}%`, bgcolor: '#4a7fbf', color: '#fff', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Gasoline {(100 * yieldSummary.gaso / (yieldSummary.dist + yieldSummary.gaso)).toFixed(0)}%
                    </Box>
                  </Box>
                </Box>
              )}

              {yieldSummary.lossRows.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  <strong>Own-use / loss streams:</strong> {yieldSummary.lossRows.map((r: BalanceRow) => `${r.product} ${fmt(Math.abs(r.production))}`).join(' · ')}
                </Typography>
              )}
            </Paper>
          )}

          {/* Fuel & Loss breakdown. ALWAYS shown, and positioned here -- directly after the Product
              slate -- because that is where Leslie asked for it ("I would like it after this"). It used
              to sit above the slate behind an "Explain this loss" toggle, collapsed by default, which is
              why she reported not seeing Fuels & Losses at all. */}
          {result.fuelLoss && <FuelLossPanel report={result.fuelLoss} series={lossSeries} />}

          {/* Independent movement reconciliation — needs the Movement report (hidden for OAS-sourced data) */}
          {result.mov && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Independent movement reconciliation (barrels, from the Movement report)</Typography>
            {result.mov.months.size > 1 && (
              <Alert severity="info" sx={{ mb: 1 }}>Movement file spans {result.mov.months.size} months — aggregated only the selected period.</Alert>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              The report's "Shipments" folds in consumption, so it reconciles to Sales + Consumption.
            </Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              {[
                ['Receipts', result.mov.totals.Receipts, result.prodTot.rec],
                ['Sales + Consumption', result.mov.totals.Sales + result.mov.totals.Consumptions, result.prodTot.ship],
                ['(of which Sales)', result.mov.totals.Sales, null],
                ['(of which Consumption)', result.mov.totals.Consumptions, null],
              ].map(([lbl, movV, prodV]: any) => (
                <Box key={lbl}>
                  <Typography variant="caption" color="text.secondary">{lbl}</Typography>
                  <Typography variant="h6">{fmt(movV)}</Typography>
                  {prodV != null && (
                    <Typography variant="caption" color={Math.abs(movV - prodV) < Math.max(1, prodV * 0.001) ? 'success.main' : 'warning.main'}>
                      {Math.abs(movV - prodV) < Math.max(1, prodV * 0.001) ? '✓ matches report' : `Δ ${fmt(movV - prodV)} vs report`}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
          )}

          {/* Measured inventory reconciliation (only when both EOM snapshots are present) */}
          {result.measured && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.main' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Measured mass balance — refinery total ({volUnit}), from tank gauging
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Opening {result.measured.openDate} → Closing {result.measured.closeDate}. Independently-measured
                inventory (not the site's own reconciled numbers), so this residual is a <strong>real gain/loss</strong>.
              </Typography>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                {[
                  ['Opening (gauged)', result.measured.invOpen],
                  ['+ Receipts', result.measured.receipts],
                  ['+ Production', result.measured.production],
                  ['− Sales', -result.measured.sales],
                  ['− Consumption', -result.measured.consumption],
                  ['− Reclass', -result.measured.reclass],
                  ['= Theoretical close', result.measured.theoreticalClose],
                  ['Actual close (gauged)', result.measured.invClose],
                ].map(([lbl, v]: any) => (
                  <Box key={lbl}>
                    <Typography variant="caption" color="text.secondary">{lbl}</Typography>
                    <Typography variant="h6">{fmt(v)}</Typography>
                  </Box>
                ))}
                <Box>
                  <Typography variant="caption" color="text.secondary">Gain / Loss</Typography>
                  <Typography variant="h6" sx={{ color: Math.abs(result.measured.gainLoss) < 1 ? 'text.disabled' : 'warning.main', fontWeight: 700 }}>
                    {fmt(result.measured.gainLoss)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Balance table */}
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Product', 'Opening', 'Receipts', 'Production', 'Shipments', 'Reclass', 'Theoretical Close', 'Actual Close', 'Gain/Loss'].map((h, i) => (
                    <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{h}{i > 0 ? ` (${unit})` : ''}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g) => g.rows.length === 0 ? null : (
                  <React.Fragment key={g.key}>
                    <TableRow id={`mbsec-${g.key}`} sx={{ scrollMarginTop: 80, '& td': { fontWeight: 700, bgcolor: 'grey.200', color: 'text.secondary', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                      <TableCell colSpan={9}>{g.label} · {g.rows.length}</TableCell>
                    </TableRow>
                    {g.rows.map((r) => (
                      <TableRow key={r.product} hover><TableCell>{r.product}</TableCell>{numCells(r)}</TableRow>
                    ))}
                    <TableRow sx={{ '& td': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                      <TableCell>{g.label} subtotal</TableCell>{numCells(sub(g.rows), true)}
                    </TableRow>
                  </React.Fragment>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 800, bgcolor: 'action.selected' } }}>
                  <TableCell>REFINERY TOTAL</TableCell>{numCells(totals, true)}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {viewMode === 'trends' && (
        <Box sx={{ mt: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">Granularity:</Typography>
              <ToggleButtonGroup size="small" exclusive value={gran} onChange={(_e, v) => v && setGran(v)}>
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="quarter">Quarter</ToggleButton>
                <ToggleButton value="annual">Annual</ToggleButton>
                <ToggleButton value="rolling12">12-mo rolling</ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="The dataset's Mass Balance Target — set it in Settings → Mass Balance. Periods beyond it are counted as outside target.">
                <Chip size="small" variant="outlined" label={`Target ±${warnPct}%`} />
              </Tooltip>
              <TextField size="small" type="number" label="Critical %" value={critPct} sx={{ width: 100 }}
                onChange={(e) => setCrit(Number(e.target.value))} inputProps={{ step: 0.1, min: 0 }} />
              <ToggleButtonGroup size="small" exclusive value={basis} onChange={(_e, v) => { if (!v) return; setBasis(v); genTrends(v); }}>
                <ToggleButton value="volume">{volToggle}</ToggleButton>
                <ToggleButton value="mass">{massToggle}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Date range:</Typography>
              <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={effFrom}
                inputProps={{ min: coverage?.from, max: coverage?.to }} onChange={(e) => setFromDate(e.target.value)} sx={{ width: 158 }} />
              <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={effTo}
                inputProps={{ min: coverage?.from, max: coverage?.to }} onChange={(e) => setToDate(e.target.value)} sx={{ width: 158 }} />
              {coverage && (
                <ButtonGroup size="small" variant="outlined">
                  <Button onClick={() => setQuickRange(1)}>Month</Button>
                  <Button onClick={() => setQuickRange(3)}>Quarter</Button>
                  <Button onClick={() => setQuickRange(12)}>Year</Button>
                  <Button onClick={() => setQuickRange(0)}>All</Button>
                </ButtonGroup>
              )}
              {coverage && (
                <Typography variant="caption" color={coverage.gaps.length ? 'warning.main' : 'text.secondary'}>
                  Uploaded {coverage.minM} → {coverage.maxM}{coverage.gaps.length ? ` · gaps: ${coverage.gaps.join(', ')}` : ' · continuous'}
                </Typography>
              )}
            </Stack>
            {(isDaily ? !movDaily.length : !buckets.length) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {loading ? 'Loading months…'
                  : isDaily ? 'No dated movement data in range — upload movement files or widen the range.'
                  : (oasPeriods.length && !periods.length) ? 'No saved months in the selected range — widen the range.'
                  : 'No months in range — add production + movement files or widen the range.'}
              </Typography>
            )}
            {isDaily && movDaily.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Day/Week views use dated <strong>movement</strong> data (throughput). Production &amp; closure need daily production reports.</Typography>}
          </Paper>

          {!isDaily && macro && (<>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              {[
                ['Periods', `${macro.n}`, `${macro.first} → ${macro.last}`, undefined],
                ['Avg loss %', `${macro.avgClosure.toFixed(3)}%`,
                  macro.hasRealLoss ? 'unaccounted loss ÷ crude, per period' : 'stock-ledger residual (no fuel & loss source)', undefined],
                ['Overall loss %', `${macro.overallClosure.toFixed(3)}%`,
                  macro.hasRealLoss ? 'across all periods · physical loss' : 'across all periods · ledger residual', undefined],
                ['Net products produced', fmt(macro.tot.netProduction), `${unit} · excludes feedstock`, undefined],
                ['Outside target', `${alerts.length}`, `${critCount} critical · > ±${warnPct}%`, alerts.length ? (critCount ? 'error.main' : 'warning.main') : undefined],
              ].map(([k, v, n, color]: any) => (
                <Paper key={k} variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography variant="h6" sx={color ? { color } : undefined}>{v}</Typography>
                  <Typography variant="caption" color="text.secondary">{n}</Typography>
                </Paper>
              ))}
            </Stack>

            {alerts.length > 0 && (
              <Alert severity={critCount ? 'error' : 'warning'} sx={{ mb: 2 }}>
                <strong>{alerts.length}</strong> period{alerts.length > 1 ? 's' : ''} outside target
                {critCount ? ` — ${critCount} grossly wrong (|loss| ≥ ${critPct}%)` : ` (|loss| > ${warnPct}%)`}:{' '}
                {alerts.slice(0, 6).map((a) => `${a.label} ${a.closurePct >= 0 ? '+' : ''}${a.closurePct.toFixed(2)}%`).join(' · ')}
                {alerts.length > 6 ? ' …' : ''}
              </Alert>
            )}

            {chartPanel}

            {drill && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.main' }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">{drill} — production over time ({unit})</Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" onClick={() => setDrill(null)}>Clear</Button>
                </Stack>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={drillSeries} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)} />
                    <RTooltip formatter={(v: any) => fmt(Number(v))} />
                    <Line dataKey="production" name="Production" stroke="#2e7d32" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Product (click to drill)</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Group</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Prior ({unit})</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Latest ({unit})</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Δ</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Δ%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movers.filter((r: any) => chartFilterProduct(r.product)).map((r) => (
                    <TableRow key={r.product} hover selected={drill === r.product} sx={{ cursor: 'pointer' }} onClick={() => setDrill(r.product)}>
                      <TableCell>{r.product}</TableCell>
                      <TableCell><Chip size="small" label={productGroup(r.product).label} variant="outlined" sx={{ borderColor: CAT_COLOR[productGroup(r.product).cat] || undefined }} /></TableCell>
                      <TableCell align="right">{fmt(r.prev)}</TableCell>
                      <TableCell align="right">{fmt(r.cur)}</TableCell>
                      <TableCell align="right" sx={{ color: Math.abs(r.delta) < 1 ? 'text.disabled' : (r.delta > 0 ? 'success.main' : 'error.main'), fontWeight: 600 }}>
                        {r.delta > 0 ? <ArrowUpward sx={{ fontSize: 12, verticalAlign: 'middle' }} /> : r.delta < 0 ? <ArrowDownward sx={{ fontSize: 12, verticalAlign: 'middle' }} /> : null}{fmt(r.delta)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>{r.prev ? `${r.pct > 0 ? '+' : ''}${r.pct.toFixed(1)}%` : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Δ compares the latest {gran === 'month' ? 'month' : gran === 'rolling12' ? 'rolling window' : gran} to the prior one, sorted by biggest movers. Click a row to chart its trend. Loss % = unaccounted loss ÷ crude processed, from the site's own fuel &amp; loss accounting. Where a period has no fuel &amp; loss source it falls back to the stock-ledger residual, which nets to ~0 by construction.
            </Typography>
          </>)}

          {isDaily && movMacro && (<>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              {[
                [gran === 'week' ? 'Weeks' : 'Days', `${movMacro.n}`, `${movMacro.first} → ${movMacro.last}`],
                ['Total receipts', fmt(movMacro.tot.receipts), unit],
                ['Total sales', fmt(movMacro.tot.sales), unit],
                ['Total consumption', fmt(movMacro.tot.consumption), unit],
              ].map(([k, v, n]: any) => (
                <Paper key={k} variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography variant="h6">{v}</Typography>
                  <Typography variant="caption" color="text.secondary">{n}</Typography>
                </Paper>
              ))}
            </Stack>

            {chartPanel}

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Product (net movement)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Prior ({unit})</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Latest ({unit})</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Δ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movMovers.filter((r: any) => chartFilterProduct(r.product)).map((r) => (
                    <TableRow key={r.product} hover>
                      <TableCell>{r.product}</TableCell>
                      <TableCell align="right">{fmt(r.prev)}</TableCell>
                      <TableCell align="right">{fmt(r.cur)}</TableCell>
                      <TableCell align="right" sx={{ color: Math.abs(r.delta) < 1 ? 'text.disabled' : (r.delta > 0 ? 'success.main' : 'error.main'), fontWeight: 600 }}>
                        {r.delta > 0 ? <ArrowUpward sx={{ fontSize: 12, verticalAlign: 'middle' }} /> : r.delta < 0 ? <ArrowDownward sx={{ fontSize: 12, verticalAlign: 'middle' }} /> : null}{fmt(r.delta)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Net movement = receipts − sales − consumption per product. Δ compares the latest {gran} to the prior one.
            </Typography>
          </>)}
        </Box>
      )}

      <MonthEndChecklist open={checklistOpen} onClose={() => setChecklistOpen(false)}
        dataset={dataset || ''} period={result?.key || ''} utility="refinery" />

      {mapping && (
        <MassBalanceMapping open={mappingOpen} onClose={() => setMappingOpen(false)}
          products={allProducts} mapping={mapping} onChange={setMapping} onSave={saveMappingNow}
          suggest={(name) => oasAssignments?.[name] || heuristicSubCat(name) || ''} />
      )}

      <Dialog open={rmgrOpen} onClose={() => setRmgrOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Saved uploads</DialogTitle>
        <DialogContent dividers>
          {rperiods.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No saved uploads yet. Files you upload are stored here automatically, grouped by month.</Typography>
          ) : (
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ minWidth: 130 }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>Month</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {rperiods.map((p) => (
                    <Button key={p} size="small" variant={p === rmgrPeriod ? 'contained' : 'text'}
                      onClick={() => selectRPeriod(p)} sx={{ justifyContent: 'flex-start' }}>{p}</Button>
                  ))}
                </Stack>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ flex: 1 }}>
                {rmgrLoading ? (
                  <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>
                ) : rmgrFiles.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No files for this month.</Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {rmgrFiles.map((sf) => (
                      <Stack key={sf.uploadId} direction="row" alignItems="center" spacing={1}>
                        <Chip size="small" label={sf.kind} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                        <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sf.filename}>{sf.filename}</Typography>
                        <Tooltip title="Delete this file"><IconButton size="small" onClick={() => removeRStored(sf)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRmgrOpen(false)}>Close</Button>
          <Button variant="contained" onClick={loadRMonth} disabled={!rmgrPeriod || rmgrLoading || rmgrFiles.length === 0}>Load this month</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!storeMsg} autoHideDuration={4000} onClose={() => setStoreMsg('')}
        message={storeMsg} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
});

export default MassBalanceUtility;
